<template>
    <el-dialog
        :visible="visible"
        title="Chi tiết hồ sơ ứng viên"
        width="800px"
        custom-class="rcv-dialog rcv-detail-dialog"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="close"
        @opened="onOpened"
        @closed="onClosed"
    >
        <div v-if="candidate" class="rcv-detail">
            <!-- Tiến trình ứng viên -->
            <cv-stage-progress ref="stageProgressRef" :status="candidate.status" :visible="visible" />

            <!-- Header tóm tắt -->
            <div class="rcv-detail__head">
                <div class="rcv-detail__head-left">
                    <span class="rcv-detail__id">Hồ sơ #{{ candidate.id }}</span>
                    <h4 class="rcv-detail__name">{{ candidate.name || "Chưa có tên" }}</h4>
                </div>
                <div class="rcv-detail__head-right">
                    <span class="rcv-detail__type-badge" :class="`rcv-detail__type-badge--${candidate.type}`">
                        {{ typeLabel(candidate.type) }}
                    </span>
                    <span
                        v-if="candidate.source_type"
                        class="rcv-detail__source-badge"
                        :class="Number(candidate.source_type) === 1 ? 'rcv-detail__source-badge--apply' : 'rcv-detail__source-badge--web'"
                    >
                        <i :class="Number(candidate.source_type) === 1 ? 'el-icon-user-solid' : 'el-icon-connection'"></i>
                        {{ Number(candidate.source_type) === 1 ? 'Tự apply' : 'Trên mạng' }}
                    </span>
                </div>
            </div>

            <!-- Thanh tác vụ Duyệt hồ sơ & Cập nhật tiến trình -->
            <div class="rcv-detail__approval-bar">
                <div class="rcv-detail__approval-left">
                    <span class="rcv-detail__approval-badge">
                        <i class="el-icon-s-check"></i>
                        <span>Duyệt hồ sơ:</span>
                    </span>
                </div>

                <div class="rcv-detail__approval-right">
                    <!-- Nút Duyệt / Từ chối theo phân quyền:
                         department_id = 3 (isHrDepartment) -> HR duyệt & từ chối
                         department_id != 3 (!isHrDepartment) -> Teamlead duyệt & từ chối -->
                    <template v-if="isHrDepartment">
                        <el-button
                            v-if="candidateStatus === HR_CAN_DUYET"
                            size="small"
                            type="success"
                            icon="el-icon-s-promotion"
                            :loading="submitting"
                            @click="handleApproveClick('hr', 1)"
                        >
                            Gửi duyệt CV (Chuyển Teamlead)
                        </el-button>
                        <el-button
                            v-if="candidateStatus === TEAM_LEAD_CAN_DUYET"
                            size="small"
                            type="primary"
                            icon="el-icon-date"
                            :loading="submitting"
                            @click="openInterviewScheduleDialog(interviewScheduleForm.round || 1)"
                        >
                            {{ hasScheduleInput ? 'Sửa lịch phỏng vấn' : 'Đặt lịch phỏng vấn' }}
                        </el-button>
                        <el-button
                            v-if="candidateStatus < TEAM_LEAD_CAN_DUYET && candidateStatus !== HR_TU_CHOI && candidateHrApprove !== 2"
                            size="small"
                            type="danger"
                            plain
                            icon="el-icon-close"
                            :loading="submitting"
                            @click="handleApproveClick('hr', 2)"
                        >
                            HR Từ chối
                        </el-button>
                    </template>
                    <template v-else>
                        <el-button
                            v-if="candidateStatus !== TEAM_LEAD_TU_CHOI && candidateTeamleadApprove !== 1"
                            size="small"
                            type="success"
                            icon="el-icon-check"
                            :loading="submitting"
                            @click="handleApproveClick('teamlead', 1)"
                        >
                            Teamlead Duyệt
                        </el-button>
                        <el-button
                            v-if="candidateStatus !== TEAM_LEAD_TU_CHOI && candidateTeamleadApprove === 1"
                            size="small"
                            plain
                            icon="el-icon-date"
                            :loading="submitting"
                            @click="openInterviewScheduleDialog"
                        >
                            Đổi lịch phỏng vấn
                        </el-button>
                        <el-button
                            v-if="candidateStatus !== TEAM_LEAD_TU_CHOI && candidateTeamleadApprove !== 2 && candidateTeamleadApprove !== 1"
                            size="small"
                            type="danger"
                            plain
                            icon="el-icon-close"
                            :loading="submitting"
                            @click="handleApproveClick('teamlead', 2)"
                        >
                            Teamlead Từ chối
                        </el-button>
                    </template>

                    <!-- Tiến trình: Phỏng vấn V1 (5) hoặc đã có kết quả V1 (7, 8) -->
                    <template v-if="candidateStatus === PHONG_VAN_V1 || candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS || candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V1_FAIL">
                        <el-button
                            size="small"
                            type="primary"
                            icon="el-icon-chat-dot-round"
                            :loading="submitting"
                            @click="$emit('open-interview', { candidate, round: 1 })"
                        >
                            {{ candidateStatus === PHONG_VAN_V1 ? 'Điền kết quả PV V1' : 'Sửa kết quả PV V1' }}
                        </el-button>
                        <el-button
                            v-if="isHrDepartment && candidateStatus === PHONG_VAN_V1"
                            size="small"
                            type="danger"
                            plain
                            icon="el-icon-circle-close"
                            :loading="submitting"
                            @click="handleCancelInterview(1)"
                        >
                            Huỷ lịch PV V1
                        </el-button>
                        <!-- Khi ở trạng thái Pass V1 (7): Nút Xếp lịch PV V2 và Điền kết quả PV V2 -->
                        <el-button
                            v-if="candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS"
                            size="small"
                            type="primary"
                            icon="el-icon-date"
                            :loading="submitting"
                            @click="openInterviewScheduleDialog(2)"
                        >
                            Xếp lịch PV V2
                        </el-button>
                        <el-button
                            v-if="candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS"
                            size="small"
                            type="warning"
                            plain
                            icon="el-icon-chat-line-square"
                            :loading="submitting"
                            @click="$emit('open-interview', { candidate, round: 2 })"
                        >
                            Điền kết quả PV V2
                        </el-button>
                    </template>

                    <!-- Tiến trình: Phỏng vấn V2 (9), đã có kết quả V2 (11, 12) -->
                    <template v-else-if="candidateStatus === PHONG_VAN_V2 || candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V2_PASS || candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V2_FAIL">
                        <el-button
                            size="small"
                            type="warning"
                            icon="el-icon-chat-line-square"
                            :loading="submitting"
                            @click="$emit('open-interview', { candidate, round: 2 })"
                        >
                            {{ candidateStatus === PHONG_VAN_V2 ? 'Điền kết quả PV V2' : 'Sửa kết quả PV V2' }}
                        </el-button>
                        <el-button
                            v-if="isHrDepartment && candidateStatus === PHONG_VAN_V2"
                            size="small"
                            type="danger"
                            plain
                            icon="el-icon-circle-close"
                            :loading="submitting"
                            @click="handleCancelInterview(2)"
                        >
                            Huỷ lịch PV V2
                        </el-button>
                    </template>

                    <!-- Tiến trình: Cần điền Offer (13) -->
                    <template v-else-if="candidateStatus === CAN_DIEN_OFFER">
                        <el-button
                            v-if="!isHrDepartment"
                            size="small"
                            type="primary"
                            icon="el-icon-edit-outline"
                            :loading="submitting"
                            @click="openOfferDialog"
                        >
                            Điền thông tin Offer (Teamlead)
                        </el-button>
                        <el-button
                            v-if="!isHrDepartment"
                            size="small"
                            plain
                            icon="el-icon-chat-line-square"
                            :loading="submitting"
                            @click="$emit('open-interview', { candidate, round: 2 })"
                        >
                            Sửa kết quả PV V2
                        </el-button>
                    </template>

                    <!-- Tiến trình: Cần làm thủ tục Pass (14) -->
                    <template v-else-if="candidateStatus === CAN_LAM_THU_TUC_PASS">
                        <el-button
                            v-if="isHrDepartment"
                            size="small"
                            type="success"
                            icon="el-icon-s-custom"
                            :loading="submitting"
                            @click="$emit('open-work-confirmation', { candidate, hasWork: true })"
                        >
                            Xác nhận đi làm (HR)
                        </el-button>
                        <el-button
                            v-if="!isHrDepartment"
                            size="small"
                            type="primary"
                            plain
                            icon="el-icon-edit"
                            :loading="submitting"
                            @click="openOfferDialog"
                        >
                            Sửa thông tin Offer
                        </el-button>
                    </template>

                    <!-- Tiến trình: Ứng viên không đi làm (15) -->
                    <template v-else-if="candidateStatus === UNG_VIEN_KHONG_DI_LAM">
                        <el-button
                            v-if="isHrDepartment"
                            size="small"
                            type="warning"
                            plain
                            icon="el-icon-refresh-left"
                            :loading="submitting"
                            @click="$emit('open-work-confirmation', { candidate, hasWork: false })"
                        >
                            Cập nhật trạng thái đi làm
                        </el-button>
                        <el-button
                            v-if="!isHrDepartment"
                            size="small"
                            type="primary"
                            plain
                            icon="el-icon-edit"
                            :loading="submitting"
                            @click="openOfferDialog"
                        >
                            Sửa thông tin Offer
                        </el-button>
                    </template>

                    <!-- Tiến trình: Đã gửi đào tạo hội nhập (16) -->
                    <template v-else-if="candidateStatus === DA_GUI_DAO_TAO_HOI_NHAP">
                        <el-button
                            v-if="isHrDepartment && isOfficialRecruitment"
                            size="small"
                            type="primary"
                            icon="el-icon-reading"
                            :loading="submitting"
                            @click="openTrainingDialog"
                        >
                            Nhập kết quả ĐT hội nhập (HR)
                        </el-button>
                        <el-button
                            v-if="isHrDepartment"
                            size="small"
                            type="warning"
                            plain
                            icon="el-icon-refresh-left"
                            :loading="submitting"
                            @click="$emit('open-work-confirmation', { candidate, hasWork: true })"
                        >
                            Cập nhật trạng thái đi làm
                        </el-button>
                    </template>

                    <!-- Tiến trình: Đã Huỷ lịch phỏng vấn V1 (6) hoặc V2 (10) -->
                    <template v-else-if="candidateStatus === HUY_PHONG_VAN_V1">
                        <el-button
                            size="small"
                            type="primary"
                            icon="el-icon-date"
                            :loading="submitting"
                            @click="openInterviewScheduleDialog(1)"
                        >
                            Đặt lại lịch PV V1
                        </el-button>
                    </template>
                    <template v-else-if="candidateStatus === HUY_PHONG_VAN_V2">
                        <el-button
                            size="small"
                            type="warning"
                            icon="el-icon-date"
                            :loading="submitting"
                            @click="openInterviewScheduleDialog(2)"
                        >
                            Đặt lại lịch PV V2
                        </el-button>
                    </template>

                    <!-- Tiến trình: Thử việc & QC / Cần đánh giá thử việc (Teamlead) -->
                    <template v-if="!isHrDepartment && (isProbationStage || candidateStatus === CAN_DANH_GIA_THU_VIEC || candidateStatus === DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS || candidateStatus === DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL)">
                        <el-button
                            size="small"
                            :type="candidateStatus === DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL ? 'danger' : 'success'"
                            icon="el-icon-medal"
                            :loading="submitting"
                            @click="openProbationDialog"
                        >
                            {{ candidateStatus === CAN_DANH_GIA_THU_VIEC ? 'Điền kết quả thử việc (Teamlead)' : 'Sửa kết quả thử việc (Teamlead)' }}
                        </el-button>
                    </template>

                    <!-- Tiến trình: Cần đánh giá thử việc / Đã đạt thử việc -> HR xác nhận Đã nhận việc (25) -->
                    <template v-if="isHrDepartment && (candidateStatus === DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS || candidateStatus === CAN_DANH_GIA_THU_VIEC)">
                        <el-button
                            size="small"
                            type="success"
                            icon="el-icon-trophy"
                            :loading="submitting"
                            @click="handleMarkHired"
                        >
                            Xác nhận Đã nhận việc (HR)
                        </el-button>
                    </template>

                    <!-- Dropdown chuyển trạng thái / tiến trình bất kỳ -->
                    <el-dropdown trigger="click" @command="handleSelectStatus">
                        <el-button size="small" :loading="submitting">
                            Đổi tiến trình <i class="el-icon-arrow-down el-icon--right"></i>
                        </el-button>
                        <el-dropdown-menu slot="dropdown" class="rcv-status-dropdown-menu">
                            <el-dropdown-item
                                v-for="opt in statusOptions"
                                :key="opt.value"
                                :command="opt.value"
                                :disabled="opt.value === candidateStatus || (!isHrDepartment && (opt.value === HUY_PHONG_VAN_V1 || opt.value === HUY_PHONG_VAN_V2 || opt.value === DA_NHAN_VIEC))"
                                :divided="opt.value === 0 || opt.value === 5 || opt.value === 9 || opt.value === 13 || opt.value === 16 || opt.value === 20 || opt.value === 25"
                            >
                                <span class="rcv-dropdown-dot" :style="{ backgroundColor: opt.color }"></span>
                                <span>{{ opt.label }}</span>
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </div>
            </div>

            <!-- Banner thông báo lý do từ chối nếu có -->
            <div v-if="candidate.hr_reason_reject && candidateHrApprove === 2" class="rcv-reject-banner">
                <i class="el-icon-warning"></i>
                <div class="rcv-reject-banner__content">
                    <span class="rcv-reject-banner__title">Lý do HR từ chối:</span>
                    <span>{{ candidate.hr_reason_reject }}</span>
                </div>
            </div>
            <div v-if="candidate.team_lead_reason_reject && (candidateTeamleadApprove === 2 || candidateStatus === TEAM_LEAD_TU_CHOI)" class="rcv-reject-banner">
                <i class="el-icon-warning"></i>
                <div class="rcv-reject-banner__content">
                    <span class="rcv-reject-banner__title">Lý do Teamlead từ chối:</span>
                    <span>{{ candidate.team_lead_reason_reject }}</span>
                </div>
            </div>

            <!-- Banner thông báo huỷ lịch phỏng vấn V1 hoặc V2 -->
            <div
                v-if="candidateStatus === HUY_PHONG_VAN_V1 || candidateStatus === HUY_PHONG_VAN_V2 || candidate.cancel_interview_v1_reason || candidate.cancel_interview_v2_reason"
                class="rcv-reject-banner rcv-reject-banner--cancel-interview"
            >
                <i class="el-icon-circle-close"></i>
                <div class="rcv-reject-banner__content">
                    <span class="rcv-reject-banner__title">
                        {{ candidateStatus === HUY_PHONG_VAN_V2 ? 'Lịch phỏng vấn Vòng 2 đã bị huỷ:' : 'Lịch phỏng vấn Vòng 1 đã bị huỷ:' }}
                    </span>
                    <span>
                        {{ candidate.cancel_interview_v2_reason || candidate.cancel_interview_v1_reason || candidate.cancel_reason || candidate.reason || candidate.note || "Đã huỷ lịch tham gia phỏng vấn" }}
                    </span>
                </div>
            </div>

            <!-- Tabs thông tin & Preview PDF -->
            <el-tabs v-model="activeTab" class="rcv-detail__tabs">
                <el-tab-pane label="Thông tin ứng viên" name="info">
                    <!-- Grid thông tin cá nhân -->
                    <div class="rcv-detail__grid">
                        <div class="rcv-detail__field">
                            <span class="rcv-detail__label">Họ và tên</span>
                            <span class="rcv-detail__val font-bold">{{ candidate.name || "—" }}</span>
                        </div>
                        <div class="rcv-detail__field">
                            <span class="rcv-detail__label">Số điện thoại</span>
                            <div class="rcv-detail__val rcv-detail__contact">
                                <span v-if="candidate.phone">
                                    <i class="el-icon-phone-outline"></i>
                                    <a :href="`tel:${candidate.phone}`">{{ candidate.phone }}</a>
                                    <i
                                        class="el-icon-document-copy rcv-copy-btn"
                                        title="Sao chép SĐT"
                                        @click="copyText(candidate.phone)"
                                    />
                                </span>
                                <span v-else class="rcv-muted">—</span>
                            </div>
                        </div>
                        <div class="rcv-detail__field">
                            <span class="rcv-detail__label">Email</span>
                            <div class="rcv-detail__val rcv-detail__contact">
                                <span v-if="candidate.email">
                                    <i class="el-icon-message"></i>
                                    <a :href="`mailto:${candidate.email}`">{{ candidate.email }}</a>
                                    <i
                                        class="el-icon-document-copy rcv-copy-btn"
                                        title="Sao chép Email"
                                        @click="copyText(candidate.email)"
                                    />
                                </span>
                                <span v-else class="rcv-muted">—</span>
                            </div>
                        </div>
                        <div class="rcv-detail__field">
                            <span class="rcv-detail__label">Địa chỉ</span>
                            <span class="rcv-detail__val">
                                <i v-if="candidate.address" class="el-icon-location-outline"></i>
                                {{ candidate.address || "—" }}
                            </span>
                        </div>
                        <div class="rcv-detail__field">
                            <span class="rcv-detail__label">Vị trí ứng tuyển / Job</span>
                            <span class="rcv-detail__val">
                                {{ jobTitle(candidate) ? `${jobTitle(candidate)} (#${candidate.job_id || candidate.order_id})` : (candidate.job_id || candidate.order_id ? `Job #${candidate.job_id || candidate.order_id}` : "Chưa gắn Job") }}
                            </span>
                        </div>
                        <div v-if="candidate.teamlead_id || candidate.teamlead_name || (candidate.teamlead && candidate.teamlead.name)" class="rcv-detail__field">
                            <span class="rcv-detail__label">Team Lead phụ trách</span>
                            <span class="rcv-detail__val">
                                <i class="el-icon-s-custom"></i>
                                {{ candidate.teamlead_name || (candidate.teamlead && candidate.teamlead.name) || (candidate.teamlead_id ? `ID #${candidate.teamlead_id}` : "—") }}
                            </span>
                        </div>
                        <div class="rcv-detail__field">
                            <span class="rcv-detail__label">Nguồn CV</span>
                            <span class="rcv-detail__val">
                                <span
                                    v-if="candidate.source_type"
                                    class="rcv-source-pill"
                                    :class="Number(candidate.source_type) === 1 ? 'rcv-source-pill--apply' : 'rcv-source-pill--web'"
                                >
                                    <i :class="Number(candidate.source_type) === 1 ? 'el-icon-user-solid' : 'el-icon-connection'"></i>
                                    {{ Number(candidate.source_type) === 1 ? 'Ứng viên tự apply' : 'Lấy trên mạng' }}
                                </span>
                                <span v-else class="rcv-muted">—</span>
                            </span>
                        </div>
                        <div class="rcv-detail__field">
                            <span class="rcv-detail__label">Thời gian nộp hồ sơ</span>
                            <span class="rcv-detail__val">{{ formatDate(candidate.created_at) }}</span>
                        </div>
                    </div>

                    <!-- Khối thông tin Sơ vấn ứng viên (HR) -->
                    <div
                        v-if="candidate.preliminary_info"
                        class="rcv-detail__block rcv-detail__block--preliminary"
                    >
                        <div class="rcv-detail__block-title text-emerald">
                            <i class="el-icon-chat-line-square"></i> Thông tin Sơ vấn (HR)
                        </div>
                        <div class="rcv-detail__preliminary-body">
                            {{ candidate.preliminary_info }}
                        </div>
                    </div>

                    <!-- Khối Bổ sung lịch phỏng vấn (HR khi status == 2) -->
                    <div
                        v-if="candidateStatus === TEAM_LEAD_CAN_DUYET && isHrDepartment"
                        id="rcv-hr-interview-schedule-section"
                        class="rcv-detail__block rcv-detail__block--hr-schedule"
                    >
                        <div class="d-flex align-items-center justify-content-between mb-2">
                            <div class="rcv-detail__block-title text-indigo mb-0">
                                <i class="el-icon-date"></i> Bổ sung lịch phỏng vấn (HR)
                            </div>
                            <div class="d-flex align-items-center" style="gap: 8px;">
                                <el-button
                                    v-if="!interviewScheduleForm.date && !interviewScheduleForm.start_time"
                                    size="mini"
                                    type="text"
                                    icon="el-icon-magic-stick"
                                    @click="fillDefaultSchedule"
                                >
                                    + Điền nhanh giờ gợi ý
                                </el-button>
                                <el-button
                                    v-else
                                    size="mini"
                                    type="text"
                                    class="text-danger"
                                    icon="el-icon-delete"
                                    @click="clearScheduleFields"
                                >
                                    Xoá lịch đã chọn
                                </el-button>
                            </div>
                        </div>

                        <div class="rcv-hr-schedule-hint">
                            <i class="el-icon-info"></i>
                            <span>Hồ sơ đang ở trạng thái <b>Team lead cần duyệt</b> (status #2). HR bổ sung thông tin lịch phỏng vấn (vòng, ngày, giờ) bên dưới để xếp lịch cho ứng viên.</span>
                        </div>

                        <el-form
                            ref="hrInterviewScheduleInlineFormRef"
                            :model="interviewScheduleForm"
                            :rules="hrInterviewScheduleInlineRules"
                            label-position="top"
                            size="small"
                            class="rcv-hr-schedule-form mt-3"
                        >

                            <!-- Ngày phỏng vấn (date) -->
                            <el-form-item label="Ngày phỏng vấn" prop="date" required>
                                <el-date-picker
                                    v-model="interviewScheduleForm.date"
                                    type="date"
                                    value-format="yyyy-MM-dd"
                                    format="dd/MM/yyyy"
                                    placeholder="Chọn ngày phỏng vấn (dd/mm/yyyy)"
                                    :picker-options="interviewDatePickerOptions"
                                    clearable
                                    class="w-100"
                                />
                            </el-form-item>

                            <!-- Khung giờ bắt đầu & kết thúc (start_time, end_time) -->
                            <div class="rcv-form-row">
                                <el-form-item label="Giờ bắt đầu" prop="start_time" class="rcv-form-col" required>
                                    <el-time-select
                                        v-model="interviewScheduleForm.start_time"
                                        :picker-options="interviewTimePickerOptions"
                                        placeholder="Giờ bắt đầu"
                                        clearable
                                        class="w-100"
                                        @change="onInterviewStartTimeChange"
                                    />
                                </el-form-item>

                                <el-form-item label="Giờ kết thúc (Tuỳ chọn)" prop="end_time" class="rcv-form-col">
                                    <el-time-select
                                        v-model="interviewScheduleForm.end_time"
                                        :picker-options="{
                                            ...interviewTimePickerOptions,
                                            minTime: interviewScheduleForm.start_time,
                                        }"
                                        placeholder="Giờ kết thúc"
                                        clearable
                                        class="w-100"
                                    />
                                </el-form-item>
                            </div>

                            <!-- Preview tóm tắt lịch -->
                            <div v-if="hasScheduleInput" class="rcv-interview-schedule-preview mb-3">
                                <i class="el-icon-check text-success"></i>
                                <span>{{ interviewSchedulePreview }}</span>
                            </div>

                            <div class="d-flex justify-content-end" style="gap: 8px;">
                                <el-button
                                    type="primary"
                                    size="small"
                                    icon="el-icon-check"
                                    :loading="submitting"
                                    @click="submitHrInterviewSchedule"
                                >
                                    Lưu lịch phỏng vấn
                                </el-button>
                            </div>
                        </el-form>
                    </div>

                    <!-- Khối thông tin Lịch phỏng vấn -->
                    <div
                        v-if="(candidateInterviewScheduleInfo || candidateStatus === HUY_PHONG_VAN_V1 || candidateStatus === HUY_PHONG_VAN_V2) && !(candidateStatus === TEAM_LEAD_CAN_DUYET && isHrDepartment)"
                        class="rcv-detail__block"
                        :class="candidateStatus === HUY_PHONG_VAN_V1 || candidateStatus === HUY_PHONG_VAN_V2 ? 'rcv-detail__block--schedule-cancelled' : 'rcv-detail__block--schedule'"
                    >
                        <div class="d-flex align-items-center justify-content-between mb-2">
                            <div class="rcv-detail__block-title mb-0" :class="candidateStatus === HUY_PHONG_VAN_V1 || candidateStatus === HUY_PHONG_VAN_V2 ? 'text-danger' : 'text-cyan'">
                                <i :class="candidateStatus === HUY_PHONG_VAN_V1 || candidateStatus === HUY_PHONG_VAN_V2 ? 'el-icon-circle-close' : 'el-icon-date'"></i>
                                {{ candidateStatus === HUY_PHONG_VAN_V1 || candidateStatus === HUY_PHONG_VAN_V2 ? 'Lịch phỏng vấn (Đã huỷ)' : 'Thông tin Lịch phỏng vấn' }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 8px;">
                                <el-button
                                    v-if="candidateStatus === PHONG_VAN_V2"
                                    size="mini"
                                    type="warning"
                                    icon="el-icon-chat-line-square"
                                    @click="$emit('open-interview', { candidate, round: 2 })"
                                >
                                    Điền kết quả PV V2
                                </el-button>
                                <el-button
                                    v-else-if="candidateStatus === PHONG_VAN_V1"
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-chat-dot-round"
                                    @click="$emit('open-interview', { candidate, round: 1 })"
                                >
                                    Điền kết quả PV V1
                                </el-button>
                                <el-button
                                    size="mini"
                                    type="text"
                                    icon="el-icon-edit"
                                    @click="openInterviewScheduleDialog(candidateStatus === HUY_PHONG_VAN_V2 ? 2 : (candidateInterviewScheduleInfo ? candidateInterviewScheduleInfo.round : 1))"
                                >
                                    {{ candidateStatus === HUY_PHONG_VAN_V1 || candidateStatus === HUY_PHONG_VAN_V2 ? 'Xếp lại lịch' : 'Đổi lịch' }}
                                </el-button>
                                <el-button
                                    v-if="isHrDepartment && (candidateStatus === PHONG_VAN_V1 || candidateStatus === PHONG_VAN_V2)"
                                    size="mini"
                                    type="text"
                                    class="text-danger"
                                    icon="el-icon-circle-close"
                                    @click="handleCancelInterview(candidateInterviewScheduleInfo ? candidateInterviewScheduleInfo.round : (candidateStatus === PHONG_VAN_V2 ? 2 : 1))"
                                >
                                    Huỷ lịch
                                </el-button>
                            </div>
                        </div>
                        <div class="rcv-detail__grid rcv-detail__grid--probation">
                            <div class="rcv-detail__field">
                                <span class="rcv-detail__label">Vòng phỏng vấn</span>
                                <span class="rcv-detail__val font-bold">
                                    {{ candidateInterviewScheduleInfo ? candidateInterviewScheduleInfo.roundText : (candidateStatus === HUY_PHONG_VAN_V2 ? 'Phỏng vấn Vòng 2' : 'Phỏng vấn Vòng 1') }}
                                </span>
                            </div>
                            <div class="rcv-detail__field">
                                <span class="rcv-detail__label">Ngày phỏng vấn</span>
                                <span class="rcv-detail__val font-bold">
                                    <i class="el-icon-calendar"></i> {{ (candidateInterviewScheduleInfo && candidateInterviewScheduleInfo.dateFormatted) || '—' }}
                                </span>
                            </div>
                            <div class="rcv-detail__field">
                                <span class="rcv-detail__label">Khung giờ</span>
                                <span class="rcv-detail__val font-bold text-primary">
                                    <i class="el-icon-time"></i> {{ (candidateInterviewScheduleInfo && candidateInterviewScheduleInfo.timeRange) || '—' }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Khối thông tin kết quả Phỏng vấn V1 (Teamlead) -->
                    <div
                        v-if="candidate.interview_v1_result || candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS || candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V1_FAIL || displayV1Feedback"
                        class="rcv-detail__block rcv-detail__block--interview-v1"
                    >
                        <div class="rcv-detail__block-title text-indigo">
                            <i class="el-icon-chat-dot-round"></i> Đánh giá Phỏng vấn V1 (Teamlead)
                        </div>
                        <div class="rcv-detail__grid rcv-detail__grid--probation">
                            <div class="rcv-detail__field">
                                <span class="rcv-detail__label">Kết quả PV V1</span>
                                <span v-if="candidate.interview_v1_result === 'pass' || candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS" class="rcv-badge-pill rcv-badge-pill--pass">
                                    <i class="el-icon-check"></i> Đạt (Pass)
                                </span>
                                <span v-else class="rcv-badge-pill rcv-badge-pill--fail">
                                    <i class="el-icon-close"></i> Không đạt (Fail)
                                </span>
                            </div>
                            <div v-if="candidate.interview_v1_score" class="rcv-detail__field">
                                <span class="rcv-detail__label">Điểm đánh giá V1</span>
                                <span class="rcv-detail__val font-bold text-primary">{{ candidate.interview_v1_score }} / 10</span>
                            </div>
                            <div v-if="displayV1Feedback" class="rcv-detail__field" style="grid-column: 1 / -1;">
                                <span class="rcv-detail__label">Nhận xét / Đánh giá của Teamlead (PV V1)</span>
                                <div v-if="parsedV1CriteriaList.length > 0" class="rcv-detail-criteria-box mt-2">
                                    <table class="rcv-detail-criteria-table">
                                        <thead>
                                            <tr>
                                                <th style="width: 32%;">Tiêu chí</th>
                                                <th style="width: 28%;" class="rcv-col-include">Bao gồm</th>
                                                <th style="width: 40%;">Đánh giá của Teamlead</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(crit, cIdx) in parsedV1CriteriaList" :key="cIdx">
                                                <td class="font-bold text-dark">{{ crit.title }}</td>
                                                <td class="text-muted text-xs font-italic rcv-col-include">{{ crit.desc }}</td>
                                                <td class="text-primary font-medium">{{ crit.evaluation }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div v-if="parsedV1GeneralNote" class="rcv-detail-criteria-extra mt-2">
                                        <b class="text-secondary">Ghi chú khác:</b> <span>{{ parsedV1GeneralNote }}</span>
                                    </div>
                                </div>
                                <div v-else class="rcv-detail__val rcv-detail__val--multiline">
                                    {{ displayV1Feedback }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Khối chờ xếp lịch Phỏng vấn V2 cho ứng viên Pass V1 (7) -->
                    <div
                        v-if="candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS"
                        class="rcv-detail__block rcv-detail__block--schedule-pending"
                    >
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <div class="rcv-detail__block-title text-success mb-1">
                                    <i class="el-icon-circle-check"></i> Đã có kết quả: Đạt Phỏng vấn Vòng 1
                                </div>
                                <div class="text-muted" style="font-size: 13px;">
                                    Ứng viên chưa được xếp lịch Phỏng vấn Vòng 2. Vui lòng bấm <b>Xếp lịch PV V2</b> để nhập ngày &amp; giờ PV Vòng 2 và chuyển sang tiến trình <b>Phỏng vấn V2 (#9)</b>.
                                </div>
                            </div>
                            <div class="d-flex align-items-center" style="gap: 8px;">
                                <el-button
                                    size="small"
                                    type="primary"
                                    icon="el-icon-date"
                                    :loading="submitting"
                                    @click="openInterviewScheduleDialog(2)"
                                >
                                    Xếp lịch PV V2
                                </el-button>
                            </div>
                        </div>
                    </div>
                    <!-- Khối thông tin kết quả Phỏng vấn V2 (Teamlead) -->
                    <div
                        v-if="candidate.interview_v2_result || candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V2_PASS || candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V2_FAIL || candidateStatus === CAN_DIEN_OFFER"
                        class="rcv-detail__block rcv-detail__block--interview-v2"
                    >
                        <div class="rcv-detail__block-title text-purple">
                            <i class="el-icon-chat-line-square"></i> Đánh giá Phỏng vấn V2 (Teamlead)
                        </div>
                        <div class="rcv-detail__grid rcv-detail__grid--probation">
                            <div class="rcv-detail__field">
                                <span class="rcv-detail__label">Kết quả PV V2</span>
                                <span v-if="candidate.interview_v2_result === 'pass' || candidateStatus === DA_NHAP_KET_QUA_PHONG_VAN_V2_PASS || candidateStatus === CAN_DIEN_OFFER" class="rcv-badge-pill rcv-badge-pill--pass">
                                    <i class="el-icon-check"></i> Đạt (Pass)
                                </span>
                                <span v-else class="rcv-badge-pill rcv-badge-pill--fail">
                                    <i class="el-icon-close"></i> Không đạt (Fail)
                                </span>
                            </div>
                            <div v-if="candidate.interview_v2_score" class="rcv-detail__field">
                                <span class="rcv-detail__label">Điểm đánh giá V2</span>
                                <span class="rcv-detail__val font-bold text-purple">{{ candidate.interview_v2_score }} / 10</span>
                            </div>
                            <div v-if="candidate.interview_v2_note" class="rcv-detail__field" style="grid-column: 1 / -1;">
                                <span class="rcv-detail__label">Nhận xét / Đánh giá của Teamlead</span>
                                <span class="rcv-detail__val">{{ candidate.interview_v2_note }}</span>
                            </div>
                        </div>
                    </div>


                    <!-- Khối thông tin Thư mời nhận việc (Offer) đã nhập -->
                    <div
                        v-if="candidateHasOfferInfo"
                        class="rcv-detail__block rcv-detail__block--offer"
                    >
                        <div class="d-flex align-items-center justify-content-between mb-2">
                            <div class="rcv-detail__block-title text-teal mb-0">
                                <i class="el-icon-document-checked"></i> Thông tin Thư mời nhận việc (Offer)
                            </div>
                            <el-button
                                v-if="!isHrDepartment"
                                size="mini"
                                type="text"
                                icon="el-icon-edit"
                                :loading="submitting"
                                @click="openOfferDialog"
                            >
                                Sửa Offer
                            </el-button>
                        </div>
                        <div class="rcv-detail__grid rcv-detail__grid--probation">
                            <div class="rcv-detail__field">
                                <span class="rcv-detail__label">Mức lương</span>
                                <span v-if="candidate.salary || candidate.expected_salary" class="rcv-detail__val font-bold text-success">
                                    <i class="el-icon-money"></i> {{ formatMoney(candidate.salary || candidate.expected_salary) }} VNĐ
                                </span>
                                <span v-else class="rcv-muted">—</span>
                            </div>
                            <div class="rcv-detail__field">
                                <span class="rcv-detail__label">Ngày thử việc</span>
                                <span v-if="candidate.probation_start_date || candidate.start_date || candidate.probation_date" class="rcv-detail__val font-bold text-primary">
                                    <i class="el-icon-date"></i> {{ formatDate(candidate.probation_start_date || candidate.start_date || candidate.probation_date) }}
                                </span>
                                <span v-else class="rcv-muted">—</span>
                            </div>
                            <div class="rcv-detail__field">
                                <span class="rcv-detail__label">Ngày kết thúc thử việc</span>
                                <span v-if="candidate.probation_end_date" class="rcv-detail__val font-bold text-indigo">
                                    <i class="el-icon-date"></i> {{ formatDate(candidate.probation_end_date) }}
                                </span>
                                <span v-else class="rcv-muted">—</span>
                            </div>
                            <div v-if="candidate.offer_note" class="rcv-detail__field" style="grid-column: 1 / -1;">
                                <span class="rcv-detail__label">Ghi chú Offer &amp; Thử việc</span>
                                <span class="rcv-detail__val">{{ candidate.offer_note }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Khối hành động nhanh: Cần làm thủ tục Pass (#14) - Xác nhận đi làm -->
                    <div
                        v-if="candidateStatus === CAN_LAM_THU_TUC_PASS"
                        class="rcv-detail__block rcv-detail__block--pass-procedure-pending"
                    >
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <div class="rcv-detail__block-title text-teal mb-1">
                                    <i class="el-icon-s-custom"></i> Tiến trình: Cần làm thủ tục Pass (#14)
                                </div>
                                <div class="text-muted" style="font-size: 13px;">
                                    Ứng viên đã có thông tin Offer. Vui lòng xác nhận ứng viên <b>Có đi làm</b> (chuyển sang Đã gửi đào tạo hội nhập) hoặc <b>Không đi làm</b>.
                                </div>
                            </div>
                            <div v-if="isHrDepartment" class="d-flex align-items-center" style="gap: 8px;">
                                <el-button
                                    size="small"
                                    type="success"
                                    icon="el-icon-check"
                                    :loading="submitting"
                                    @click="$emit('open-work-confirmation', { candidate, hasWork: true })"
                                >
                                    Có đi làm
                                </el-button>
                                <el-button
                                    size="small"
                                    type="danger"
                                    plain
                                    icon="el-icon-close"
                                    :loading="submitting"
                                    @click="$emit('open-work-confirmation', { candidate, hasWork: false })"
                                >
                                    Không đi làm
                                </el-button>
                            </div>
                        </div>
                    </div>

                    <!-- Khối thông báo: Đã gửi đào tạo hội nhập (#16) -->
                    <div
                        v-else-if="candidateStatus === DA_GUI_DAO_TAO_HOI_NHAP"
                        class="rcv-detail__block"
                        style="background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 12px 16px;"
                    >
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <div class="rcv-detail__block-title mb-1" style="color: #d97706 !important;">
                                    <i class="el-icon-reading"></i> Tiến trình: Đã gửi đào tạo hội nhập (#16)
                                </div>
                                <div class="text-muted" style="font-size: 13px;">
                                    Ứng viên đã được xác nhận <b>Có đi làm</b> và chuyển sang danh sách <b>Đào tạo hội nhập</b>.
                                </div>
                            </div>
                            <div v-if="isHrDepartment" class="d-flex align-items-center" style="gap: 8px;">
                                <el-button
                                    v-if="isOfficialRecruitment"
                                    size="small"
                                    type="primary"
                                    icon="el-icon-reading"
                                    :loading="submitting"
                                    @click="openTrainingDialog"
                                >
                                    Nhập kết quả ĐT hội nhập
                                </el-button>
                                <el-button
                                    size="small"
                                    type="warning"
                                    plain
                                    icon="el-icon-refresh-left"
                                    :loading="submitting"
                                    @click="$emit('open-work-confirmation', { candidate, hasWork: true })"
                                >
                                    Đổi trạng thái đi làm
                                </el-button>
                            </div>
                        </div>
                    </div>

                    <!-- Khối thông báo: Ứng viên không đi làm (#15) -->
                    <div
                        v-else-if="candidateStatus === UNG_VIEN_KHONG_DI_LAM"
                        class="rcv-detail__block"
                        style="background: #faf5ff; border: 1px solid #e9d5ff; border-radius: 8px; padding: 12px 16px;"
                    >
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <div class="rcv-detail__block-title mb-1" style="color: #9333ea !important;">
                                    <i class="el-icon-warning-outline"></i> Tiến trình: Ứng viên không đi làm (#15)
                                </div>
                                <div class="text-muted" style="font-size: 13px;">
                                    Ứng viên đã được xác nhận <b>Không đi làm</b>.
                                    <span v-if="candidate.probation_reason"> Lý do: <b class="text-danger">{{ candidate.probation_reason }}</b></span>
                                </div>
                            </div>
                            <div v-if="isHrDepartment" class="d-flex align-items-center" style="gap: 8px;">
                                <el-button
                                    size="small"
                                    type="success"
                                    plain
                                    icon="el-icon-refresh-left"
                                    :loading="submitting"
                                    @click="$emit('open-work-confirmation', { candidate, hasWork: false })"
                                >
                                    Đổi trạng thái đi làm
                                </el-button>
                            </div>
                        </div>
                    </div>

                    <!-- Khối thông tin kết quả thử việc & Mức lương -->
                    <div v-if="candidate.probation_result || candidate.official_salary || candidate.probation_date || candidate.probation_reason || candidate.probation_exit_date || candidate.probation_exit_reason || candidateStatus === 23 || candidateStatus === 24" class="rcv-detail__block rcv-detail__block--probation">
                        <div class="rcv-detail__block-title text-primary">
                            <i class="el-icon-s-claim"></i> Kết quả thử việc & Mức lương chính thức
                        </div>
                        <div class="rcv-detail__grid rcv-detail__grid--probation">
                            <div class="rcv-detail__field">
                                <span class="rcv-detail__label">Kết quả thử việc</span>
                                <span v-if="candidate.probation_result === 'pass' || candidateStatus === 23" class="rcv-badge-pill rcv-badge-pill--pass">
                                    <i class="el-icon-check"></i> Đạt (Pass)
                                </span>
                                <span v-else-if="candidate.probation_result === 'failed' || candidateStatus === 24 || candidate.probation_exit_date || candidate.probation_exit_reason" class="rcv-badge-pill rcv-badge-pill--fail">
                                    <i class="el-icon-close"></i> Không đạt (Failed)
                                </span>
                                <span v-else class="rcv-muted">—</span>
                            </div>
                            <div class="rcv-detail__field">
                                <span class="rcv-detail__label">Mức lương chính thức</span>
                                <span v-if="candidate.official_salary" class="rcv-detail__val font-bold text-success">
                                    {{ formatMoney(candidate.official_salary) }} VNĐ
                                </span>
                                <span v-else class="rcv-muted">—</span>
                            </div>
                            <div v-if="candidate.probation_date" class="rcv-detail__field">
                                <span class="rcv-detail__label">Ngày thử việc</span>
                                <span class="rcv-detail__val font-bold">
                                    <i class="el-icon-date"></i> {{ formatDate(candidate.probation_date) }}
                                </span>
                            </div>
                            <div v-if="candidate.probation_exit_date" class="rcv-detail__field">
                                <span class="rcv-detail__label text-danger">Ngày out thử việc</span>
                                <span class="rcv-detail__val font-bold text-danger">
                                    <i class="el-icon-date"></i> {{ formatDate(candidate.probation_exit_date) }}
                                </span>
                            </div>
                            <div v-if="candidate.probation_exit_reason" class="rcv-detail__field" style="grid-column: 1 / -1;">
                                <span class="rcv-detail__label text-danger">Lý do out thử việc</span>
                                <span class="rcv-detail__val text-danger font-medium">{{ candidate.probation_exit_reason }}</span>
                            </div>
                            <div v-if="candidate.probation_reason && !candidate.probation_exit_reason" class="rcv-detail__field" style="grid-column: 1 / -1;">
                                <span class="rcv-detail__label text-danger">Lý do không thử việc / không nhận việc</span>
                                <span class="rcv-detail__val text-danger font-medium">{{ candidate.probation_reason }}</span>
                            </div>
                            <div v-if="candidate.probation_note" class="rcv-detail__field" style="grid-column: 1 / -1;">
                                <span class="rcv-detail__label">Nhận xét / Ghi chú thử việc</span>
                                <span class="rcv-detail__val">{{ candidate.probation_note }}</span>
                            </div>
                            <div v-if="candidateStatus === 25 || candidateStatus === DA_NHAN_VIEC" class="rcv-detail__field" style="grid-column: 1 / -1;">
                                <span class="rcv-detail__label">Trạng thái nhân sự</span>
                                <span class="rcv-badge-pill rcv-badge-pill--pass font-bold">
                                    <i class="el-icon-trophy"></i> Đã nhận việc
                                </span>
                            </div>
                            <div v-else-if="isHrDepartment && (candidateStatus === 23 || candidateStatus === DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS)" class="rcv-detail__field" style="grid-column: 1 / -1;">
                                <span class="rcv-detail__label">Tiếp nhận nhân sự (HR)</span>
                                <div>
                                    <el-button
                                        size="mini"
                                        type="success"
                                        icon="el-icon-trophy"
                                        :loading="submitting"
                                        @click="handleMarkHired"
                                    >
                                        Xác nhận Đã nhận việc
                                    </el-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Lời nhắn / Giới thiệu bản thân -->
                    <div class="rcv-detail__block">
                        <div class="rcv-detail__block-title">
                            <i class="el-icon-chat-dot-round"></i> Lời nhắn ứng tuyển
                        </div>
                        <div v-if="candidate.message" class="rcv-detail__message-box">
                            {{ candidate.message }}
                        </div>
                        <div v-else class="rcv-detail__empty-text">
                            Ứng viên không để lại lời nhắn kèm theo.
                        </div>
                    </div>

                    <!-- Danh sách file media / đính kèm -->
                    <div v-if="candidate.media && candidate.media.length" class="rcv-detail__block">
                        <div class="rcv-detail__block-title">
                            <i class="el-icon-paperclip"></i> Tệp đính kèm ({{ candidate.media.length }})
                        </div>
                        <div class="rcv-detail__media-list">
                            <div v-for="m in candidate.media" :key="m.id" class="rcv-detail__media-item">
                                <i class="el-icon-document"></i>
                                <span class="rcv-detail__media-name">{{ m.name || m.file_name || `Media #${m.id}` }}</span>
                                <span v-if="m.size" class="rcv-detail__media-size">{{ formatFileSize(m.size) }}</span>
                                <a
                                    v-if="m.url || m.original_url || candidate.cv_file"
                                    :href="m.url || m.original_url || candidate.cv_file"
                                    target="_blank"
                                    class="rcv-detail__media-link"
                                >
                                    <i class="el-icon-top-right"></i> Mở tệp
                                </a>
                            </div>
                        </div>
                    </div>
                </el-tab-pane>

                <el-tab-pane name="cv">
                    <span slot="label">
                        <i class="el-icon-document"></i> Xem file CV
                        <span v-if="activePreviewUrl" class="rcv-tab-badge">PDF</span>
                    </span>
                    <div class="rcv-detail__cv-pane">
                        <!-- Toolbar file CV -->
                        <div v-if="activePreviewUrl" class="rcv-detail__cv-toolbar">
                            <span class="rcv-detail__cv-url" :title="activePreviewUrl">
                                <i class="el-icon-link"></i> {{ activePreviewUrl }}
                            </span>
                            <div class="rcv-detail__cv-btns">
                                <a
                                    :href="activePreviewUrl"
                                    target="_blank"
                                    class="el-button el-button--primary el-button--mini"
                                >
                                    <i class="el-icon-top-right"></i> Mở tab mới
                                </a>
                                <a
                                    :href="activePreviewUrl"
                                    download
                                    target="_blank"
                                    class="el-button el-button--default el-button--mini"
                                >
                                    <i class="el-icon-download"></i> Tải về
                                </a>
                            </div>
                        </div>

                        <!-- Chọn file đính kèm nếu có nhiều file -->
                        <div v-if="candidate.media && candidate.media.length > 1" class="rcv-detail__media-picker">
                            <span class="rcv-detail__media-picker-label">Chọn tệp xem:</span>
                            <el-button
                                v-for="(m, idx) in candidate.media"
                                :key="m.id || idx"
                                size="mini"
                                :type="selectedMediaUrl === (m.url || m.original_url) ? 'primary' : 'default'"
                                @click="selectedMediaUrl = m.url || m.original_url"
                            >
                                {{ m.name || m.file_name || `File ${idx + 1}` }}
                            </el-button>
                        </div>

                        <!-- Preview PDF Iframe -->
                        <div v-if="activePreviewUrl" class="rcv-detail__pdf-frame">
                            <iframe
                                :src="activePreviewUrl"
                                frameborder="0"
                                class="rcv-pdf-iframe"
                            ></iframe>
                        </div>

                        <!-- Trống nếu không có file CV -->
                        <div v-else class="rcv-detail__no-cv">
                            <i class="el-icon-document-delete"></i>
                            <p>Ứng viên chưa đính kèm file CV hoặc tệp tài liệu.</p>
                            <el-button size="small" type="primary" plain icon="el-icon-upload2" @click="handleEdit">
                                Cập nhật tải lên file CV
                            </el-button>
                        </div>
                    </div>
                </el-tab-pane>
            </el-tabs>
        </div>

        <span slot="footer" class="rcv-detail-footer">
            <div class="rcv-detail-footer__left">
                <span class="rcv-muted">
                    <i class="el-icon-time"></i> Nộp lúc: {{ formatDate(candidate.created_at) }}
                </span>
            </div>
            <div class="rcv-detail-footer__right">
                <el-button
                    v-if="!isHrDepartment && (isProbationStage || candidateStatus === CAN_DANH_GIA_THU_VIEC || candidateStatus === DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS || candidateStatus === DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL)"
                    size="small"
                    :type="candidateStatus === DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL ? 'danger' : 'success'"
                    icon="el-icon-medal"
                    @click="openProbationDialog"
                >
                    {{ candidateStatus === CAN_DANH_GIA_THU_VIEC ? 'Điền kết quả thử việc (Teamlead)' : 'Sửa kết quả thử việc (Teamlead)' }}
                </el-button>
                <el-button size="small" type="primary" icon="el-icon-edit" @click="handleEdit">
                    Chỉnh sửa hồ sơ
                </el-button>
                <el-button size="small" @click="close">Đóng</el-button>
            </div>
        </span>

        <!-- Dialog Điền kết quả thử việc -->
        <el-dialog
            :visible.sync="probationDialogVisible"
            title="Điền kết quả thử việc"
            width="520px"
            custom-class="rcv-dialog"
            :close-on-click-modal="false"
            append-to-body
        >
            <div v-if="candidate" class="rcv-probation-dialog">
                <div class="rcv-probation-cand">
                    <div class="rcv-probation-cand__name">{{ candidate.name }}</div>
                    <div class="rcv-probation-cand__sub">
                        <span>SĐT: <b>{{ candidate.phone || "—" }}</b></span> · 
                        <span>Vị trí: <b>{{ jobTitle(candidate) || "—" }}</b></span>
                    </div>
                </div>

                <el-form ref="probationFormRef" :model="probationForm" :rules="probationRules" label-position="top" size="small">
                    <el-form-item label="Kết quả thử việc" prop="result" required>
                        <el-radio-group v-model="probationForm.result" size="medium" class="w-100 rcv-probation-radio">
                            <el-radio-button label="pass">
                                <i class="el-icon-check"></i> Pass (Đạt thử việc)
                            </el-radio-button>
                            <el-radio-button label="failed">
                                <i class="el-icon-close"></i> Failed (Không đạt)
                            </el-radio-button>
                        </el-radio-group>
                    </el-form-item>

                    <el-form-item label="Mức lương chính thức (VNĐ)" prop="official_salary">
                        <el-input-number
                            v-model="probationForm.official_salary"
                            :min="0"
                            :step="500000"
                            :controls="false"
                            placeholder="Nhập mức lương chính thức (VD: 15000000)..."
                            class="w-100"
                        />
                        <div v-if="probationForm.official_salary" class="rcv-form__hint">
                            Bằng chữ: <b>{{ formatMoney(probationForm.official_salary) }} VNĐ</b>
                        </div>
                    </el-form-item>

                    <el-form-item label="Nhận xét / Đánh giá chi tiết">
                        <el-input
                            v-model="probationForm.note"
                            type="textarea"
                            :rows="3"
                            placeholder="Nhập nhận xét về thái độ, năng lực, kết quả làm việc trong thời gian thử việc..."
                            maxlength="1000"
                            show-word-limit
                        />
                    </el-form-item>
                </el-form>
            </div>

            <span slot="footer" class="dialog-footer">
                <el-button size="small" @click="probationDialogVisible = false">Huỷ</el-button>
                <el-button
                    size="small"
                    type="primary"
                    :loading="submitting"
                    icon="el-icon-check"
                    @click="submitProbationResult"
                >
                    Xác nhận kết quả
                </el-button>
            </span>
        </el-dialog>

        <!-- Dialog Đặt / Bổ sung lịch phỏng vấn -->
        <el-dialog
            :visible.sync="interviewScheduleDialogVisible"
            :title="isHrDepartment ? 'Bổ sung lịch phỏng vấn ứng viên' : 'Xác nhận Teamlead duyệt hồ sơ'"
            width="540px"
            custom-class="rcv-dialog rcv-interview-schedule-dialog"
            :close-on-click-modal="false"
            append-to-body
        >
            <div v-if="candidate" class="rcv-probation-dialog">
                <!-- Thông tin ứng viên vắn tắt -->
                <div class="rcv-probation-cand">
                    <div class="rcv-probation-cand__name">
                        <i class="el-icon-user-solid text-primary mr-1"></i>
                        {{ candidate.name || "Ứng viên #" + candidate.id }}
                    </div>
                    <div class="rcv-probation-cand__sub">
                        <span>SĐT: <b>{{ candidate.phone || "—" }}</b></span> · 
                        <span>Vị trí: <b>{{ jobTitle(candidate) || "—" }}</b></span>
                    </div>
                </div>

                <!-- Khối hiển thị Thông tin sơ vấn (HR ghi nhận) -->
                <div class="rcv-preliminary-info-card">
                    <div class="rcv-preliminary-info-card__head">
                        <i class="el-icon-chat-line-square text-emerald mr-1"></i>
                        <span class="rcv-preliminary-info-card__title">Thông tin Sơ vấn (HR ghi nhận)</span>
                    </div>
                    <div class="rcv-preliminary-info-card__body">
                        <div v-if="candidate.preliminary_info || candidate.preliminary_note" class="rcv-preliminary-info-card__text">
                            {{ candidate.preliminary_info || candidate.preliminary_note }}
                        </div>
                        <div v-else class="rcv-preliminary-info-card__empty">
                            <i class="el-icon-info"></i> Chưa có thông tin sơ vấn được ghi nhận.
                        </div>
                    </div>
                </div>

                <div class="rcv-schedule-intro">
                    <i class="el-icon-info text-primary"></i>
                    {{ isHrDepartment
                        ? 'Bổ sung thông tin lịch phỏng vấn (vòng, ngày, giờ) cho ứng viên để chuyển sang tiến trình phỏng vấn.'
                        : 'Xác nhận duyệt hồ sơ ứng viên để chuyển sang tiến trình phỏng vấn. Bạn có thể nhập thời gian phỏng vấn kèm theo (không bắt buộc).' }}
                </div>

                <div class="d-flex align-items-center justify-content-between mb-2">
                    <span class="font-bold text-dark" style="font-size: 13px;">
                        <i class="el-icon-date text-primary mr-1"></i>
                        Thời gian phỏng vấn <span :class="isHrDepartment ? 'text-danger' : 'text-muted font-normal'">{{ isHrDepartment ? '(Bắt buộc)' : '(Tuỳ chọn)' }}</span>
                    </span>
                    <div>
                        <el-button
                            v-if="!interviewScheduleForm.date && !interviewScheduleForm.start_time"
                            size="mini"
                            type="text"
                            icon="el-icon-magic-stick"
                            @click="fillDefaultSchedule"
                        >
                            + Điền nhanh giờ gợi ý
                        </el-button>
                        <el-button
                            v-else
                            size="mini"
                            type="text"
                            class="text-danger"
                            icon="el-icon-delete"
                            @click="clearScheduleFields"
                        >
                            Xoá lịch đã chọn
                        </el-button>
                    </div>
                </div>

                <el-form
                    ref="interviewScheduleFormRef"
                    :model="interviewScheduleForm"
                    :rules="interviewScheduleRules"
                    label-position="top"
                    size="small"
                >

                    <!-- Ngày phỏng vấn (date) -->
                    <el-form-item :label="isHrDepartment ? 'Ngày phỏng vấn' : 'Ngày phỏng vấn (Tuỳ chọn)'" prop="date" :required="isHrDepartment">
                        <el-date-picker
                            v-model="interviewScheduleForm.date"
                            type="date"
                            value-format="yyyy-MM-dd"
                            format="dd/MM/yyyy"
                            placeholder="Chọn ngày phỏng vấn (để trống nếu chưa xếp lịch)"
                            :picker-options="interviewDatePickerOptions"
                            clearable
                            class="w-100"
                        />
                    </el-form-item>

                    <!-- Khung giờ bắt đầu & kết thúc (start_time, end_time) -->
                    <div class="rcv-form-row">
                        <el-form-item :label="isHrDepartment ? 'Giờ bắt đầu' : 'Giờ bắt đầu (Tuỳ chọn)'" prop="start_time" class="rcv-form-col" :required="isHrDepartment">
                            <el-time-select
                                v-model="interviewScheduleForm.start_time"
                                :picker-options="interviewTimePickerOptions"
                                placeholder="Bắt đầu"
                                clearable
                                class="w-100"
                                @change="onInterviewStartTimeChange"
                            />
                        </el-form-item>

                        <el-form-item label="Giờ kết thúc (Tuỳ chọn)" prop="end_time" class="rcv-form-col">
                            <el-time-select
                                v-model="interviewScheduleForm.end_time"
                                :picker-options="{
                                    ...interviewTimePickerOptions,
                                    minTime: interviewScheduleForm.start_time,
                                }"
                                placeholder="Kết thúc"
                                clearable
                                class="w-100"
                            />
                        </el-form-item>
                    </div>

                    <!-- Hộp tóm tắt trạng thái lịch -->
                    <div v-if="hasScheduleInput" class="rcv-interview-schedule-preview">
                        <i class="el-icon-check text-success"></i>
                        <span>{{ interviewSchedulePreview }} (sẽ gửi kèm khi lưu)</span>
                    </div>
                    <div v-else class="rcv-interview-schedule-preview rcv-interview-schedule-preview--empty">
                        <i class="el-icon-info"></i>
                        <span>{{ isHrDepartment ? 'Vui lòng nhập ngày và giờ bắt đầu phỏng vấn.' : 'Chưa nhập lịch phỏng vấn (hồ sơ sẽ được duyệt mà không kèm lịch).' }}</span>
                    </div>
                </el-form>
            </div>

            <span slot="footer" class="dialog-footer">
                <el-button size="small" @click="interviewScheduleDialogVisible = false">Huỷ</el-button>
                <el-button
                    size="small"
                    type="success"
                    :loading="submitting"
                    icon="el-icon-check"
                    @click="submitInterviewSchedule"
                >
                    {{ isHrDepartment ? 'Lưu lịch phỏng vấn' : (hasScheduleInput ? 'Teamlead Duyệt & Lưu lịch' : 'Xác nhận Teamlead Duyệt') }}
                </el-button>
            </span>
        </el-dialog>
    </el-dialog>
</template>

<script>
import moment from "moment-timezone";
import CvStageProgress from "./CvStageProgress.vue";
import {
    HR_CAN_DUYET,
    HR_TU_CHOI,
    TEAM_LEAD_CAN_DUYET,
    TEAM_LEAD_DA_DUYET,
    TEAM_LEAD_TU_CHOI,
    PHONG_VAN_V1,
    HUY_PHONG_VAN_V1,
    DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS,
    DA_NHAP_KET_QUA_PHONG_VAN_V1_FAIL,
    PHONG_VAN_V2,
    HUY_PHONG_VAN_V2,
    DA_NHAP_KET_QUA_PHONG_VAN_V2_PASS,
    DA_NHAP_KET_QUA_PHONG_VAN_V2_FAIL,
    CAN_DIEN_OFFER,
    CAN_LAM_THU_TUC_PASS,
    UNG_VIEN_KHONG_DI_LAM,
    DA_GUI_DAO_TAO_HOI_NHAP,
    QC_THANH_TRA_THU_VIEC_PASS,
    QC_THANH_TRA_THU_VIEC_FAIL,
    CAN_DANH_GIA_THU_VIEC,
    DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS,
    DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL,
    DA_NHAN_VIEC,
    STATUS_OPTIONS,
    getStatusMeta,
    getStatusText,
} from "@/core/config/recruitmentCvs/recruitmentCvsOption";

/** "08:00:00" | "08:00" -> "08:00" */
function toHm(value) {
    const m = value ? String(value).match(/^(\d{1,2}):(\d{2})/) : null;
    return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
}

/** "08:00" -> "08:00:00" (định dạng backend yêu cầu) */
function toHms(value) {
    const hm = toHm(value);
    return hm ? `${hm}:00` : null;
}

export default {
    name: "CvDetailDialog",
    components: {
        CvStageProgress,
    },
    props: {
        visible: { type: Boolean, default: false },
        candidate: { type: Object, default: null },
        orders: { type: Array, default: () => [] },
        initialTab: { type: String, default: "info" },
        isHrDepartment: { type: Boolean, default: true },
        submitting: { type: Boolean, default: false },
    },
    data() {
        return {
            activeTab: "info",
            selectedMediaUrl: "",
            probationDialogVisible: false,
            probationForm: {
                result: "pass",
                official_salary: null,
                note: "",
            },
            probationRules: {
                result: [
                    { required: true, message: "Vui lòng chọn kết quả thử việc", trigger: "change" },
                ],
            },
            interviewScheduleDialogVisible: false,
            interviewScheduleForm: {
                round: 1,
                date: "",
                start_time: "",
                end_time: "",
            },
            interviewDatePickerOptions: {
                firstDayOfWeek: 1,
            },
            interviewTimePickerOptions: {
                start: "07:00",
                step: "00:15",
                end: "22:00",
            },
            hrInterviewScheduleInlineRules: {
                date: [
                    { required: true, message: "Vui lòng chọn ngày phỏng vấn", trigger: "change" },
                ],
                start_time: [
                    { required: true, message: "Vui lòng chọn giờ bắt đầu phỏng vấn", trigger: "change" },
                ],
                end_time: [
                    {
                        validator: (rule, value, callback) => {
                            const f = this.interviewScheduleForm;
                            if (f.start_time && value && value <= f.start_time) {
                                callback(new Error("Giờ kết thúc phải sau giờ bắt đầu"));
                            } else {
                                callback();
                            }
                        },
                        trigger: "change",
                    },
                ],
            },
            interviewScheduleRules: {
                round: [
                    { required: true, message: "Vui lòng chọn vòng phỏng vấn", trigger: "change" },
                ],
                date: [
                    {
                        validator: (rule, value, callback) => {
                            if (this.isHrDepartment && !value) {
                                callback(new Error("Vui lòng chọn ngày phỏng vấn"));
                            } else if (this.interviewScheduleForm.start_time && !value) {
                                callback(new Error("Vui lòng chọn ngày phỏng vấn nếu đã chọn giờ bắt đầu"));
                            } else {
                                callback();
                            }
                        },
                        trigger: "change",
                    },
                ],
                start_time: [
                    {
                        validator: (rule, value, callback) => {
                            if (this.isHrDepartment && !value) {
                                callback(new Error("Vui lòng chọn giờ bắt đầu phỏng vấn"));
                            } else if (this.interviewScheduleForm.date && !value) {
                                callback(new Error("Vui lòng chọn giờ bắt đầu nếu đã chọn ngày phỏng vấn"));
                            } else {
                                callback();
                            }
                        },
                        trigger: "change",
                    },
                ],
                end_time: [
                    {
                        validator: (rule, value, callback) => {
                            const f = this.interviewScheduleForm;
                            if (f.start_time && value && value <= f.start_time) {
                                callback(new Error("Giờ kết thúc phải sau giờ bắt đầu"));
                            } else {
                                callback();
                            }
                        },
                        trigger: "change",
                    },
                ],
            },
            HR_CAN_DUYET,
            HR_TU_CHOI,
            TEAM_LEAD_CAN_DUYET,
            TEAM_LEAD_DA_DUYET,
            TEAM_LEAD_TU_CHOI,
            PHONG_VAN_V1,
            HUY_PHONG_VAN_V1,
            DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS,
            DA_NHAP_KET_QUA_PHONG_VAN_V1_FAIL,
            PHONG_VAN_V2,
            HUY_PHONG_VAN_V2,
            DA_NHAP_KET_QUA_PHONG_VAN_V2_PASS,
            DA_NHAP_KET_QUA_PHONG_VAN_V2_FAIL,
            CAN_DIEN_OFFER,
            CAN_LAM_THU_TUC_PASS,
            UNG_VIEN_KHONG_DI_LAM,
            DA_GUI_DAO_TAO_HOI_NHAP,
            QC_THANH_TRA_THU_VIEC_PASS,
            QC_THANH_TRA_THU_VIEC_FAIL,
            CAN_DANH_GIA_THU_VIEC,
            DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS,
            DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL,
            DA_NHAN_VIEC,
        };
    },
    computed: {
        displayV1Feedback() {
            const cand = this.candidate || {};
            return (cand.team_lead_feedback || cand.interview_v1_note || cand.result_note || "").trim();
        },
        parsedV1CriteriaList() {
            const raw = this.displayV1Feedback;
            if (!raw) return [];
            const hasFormat =
                /1\.\s*Năng lực chuyên môn/i.test(raw) ||
                /2\.\s*Kỹ năng mềm/i.test(raw) ||
                /3\.\s*Thái độ/i.test(raw) ||
                /4\.\s*Sự phù hợp/i.test(raw);
            if (!hasFormat) return [];

            const defs = [
                {
                    title: "1. Năng lực chuyên môn & Kỹ năng nghiệp vụ",
                    desc: "Kiến thức nghiệp vụ, kinh nghiệm làm việc và khả năng ứng dụng thực tế",
                    regex: /1\.\s*Năng lực chuyên môn[^:]*:\s*([\s\S]*?)(?=(?:2\.\s*Kỹ năng mềm|3\.\s*Thái độ|4\.\s*Sự phù hợp|Ghi chú khác|$))/i,
                },
                {
                    title: "2. Kỹ năng mềm & Giải quyết vấn đề",
                    desc: "Giao tiếp, làm việc nhóm, quản lý thời gian và giải quyết vấn đề",
                    regex: /2\.\s*Kỹ năng mềm[^:]*:\s*([\s\S]*?)(?=(?:3\.\s*Thái độ|4\.\s*Sự phù hợp|Ghi chú khác|$))/i,
                },
                {
                    title: "3. Thái độ & Tố chất cá nhân",
                    desc: "Sự chủ động, tinh thần cầu tiến, khả năng gắn bó và độ thích nghi",
                    regex: /3\.\s*Thái độ[^:]*:\s*([\s\S]*?)(?=(?:4\.\s*Sự phù hợp|Ghi chú khác|$))/i,
                },
                {
                    title: "4. Sự phù hợp văn hóa doanh nghiệp",
                    desc: "Mức độ hòa nhập với giá trị cốt lõi và môi trường làm việc của công ty",
                    regex: /4\.\s*Sự phù hợp[^:]*:\s*([\s\S]*?)(?=(?:Ghi chú khác|$))/i,
                },
            ];

            const list = [];
            for (const item of defs) {
                const match = raw.match(item.regex);
                if (match && match[1] && match[1].trim()) {
                    list.push({
                        title: item.title,
                        desc: item.desc,
                        evaluation: match[1].trim(),
                    });
                }
            }
            return list;
        },
        parsedV1GeneralNote() {
            const raw = this.displayV1Feedback;
            if (!raw) return "";
            const genMatch = raw.match(/Ghi chú khác:\s*([\s\S]*?)$/i);
            return genMatch && genMatch[1] ? genMatch[1].trim() : "";
        },
        statusOptions() {
            return STATUS_OPTIONS;
        },
        candidateStatus() {
            return this.candidate && this.candidate.status !== undefined && this.candidate.status !== null
                ? Number(this.candidate.status)
                : 0;
        },
        candidateStatusMeta() {
            return getStatusMeta(this.candidateStatus);
        },
        candidateHrApprove() {
            if (!this.candidate) return 0;
            if (Number(this.candidate.status) === HR_TU_CHOI) {
                return 2; // HR từ chối
            }
            if (this.candidate.hr_approve !== undefined && this.candidate.hr_approve !== null && this.candidate.hr_approve !== "") {
                return Number(this.candidate.hr_approve);
            }
            return 0;
        },
        candidateTeamleadApprove() {
            if (!this.candidate) return 0;
            if (Number(this.candidate.status) === TEAM_LEAD_TU_CHOI) {
                return 2; // Teamlead từ chối
            }
            if (Number(this.candidate.status) === TEAM_LEAD_DA_DUYET || Number(this.candidate.status) >= PHONG_VAN_V1) {
                return 1; // Teamlead đã duyệt
            }
            if (this.candidate.teamlead_approve !== undefined && this.candidate.teamlead_approve !== null && this.candidate.teamlead_approve !== "") {
                return Number(this.candidate.teamlead_approve);
            }
            return 0;
        },
        activePreviewUrl() {
            if (this.selectedMediaUrl) return this.selectedMediaUrl;
            if (this.candidate && this.candidate.cv_file) return this.candidate.cv_file;
            if (this.candidate && this.candidate.media && this.candidate.media[0]) {
                return this.candidate.media[0].url || this.candidate.media[0].original_url || "";
            }
            return "";
        },
        isProbationStage() {
            if (!this.candidateStatusMeta) return false;
            return (
                this.candidateStatusMeta.stageKey === "probation_qc" ||
                [
                    QC_THANH_TRA_THU_VIEC_PASS,
                    QC_THANH_TRA_THU_VIEC_FAIL,
                    CAN_DANH_GIA_THU_VIEC,
                    DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS,
                    DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL,
                ].includes(this.candidateStatus)
            );
        },
        candidateInterviewScheduleInfo() {
            if (!this.candidate) return null;
            const cand = this.candidate;
            const candStatus = Number(cand.status);
            const date =
                cand.date ||
                cand.interview_date ||
                (cand.interview_data && (cand.interview_data.date || cand.interview_data.interview_date));
            const startTime =
                cand.start_time ||
                cand.interview_start_time ||
                (cand.interview_data && (cand.interview_data.start_time || cand.interview_data.interview_start_time));
            const endTime =
                cand.end_time ||
                cand.interview_end_time ||
                (cand.interview_data && (cand.interview_data.end_time || cand.interview_data.interview_end_time));
            const round =
                cand.round ||
                (cand.interview_data && cand.interview_data.round) ||
                (candStatus === PHONG_VAN_V2 || candStatus === HUY_PHONG_VAN_V2 ? 2 : 1);

            const hasSchedule = Boolean(date || startTime);
            const isCancelled = candStatus === HUY_PHONG_VAN_V1 || candStatus === HUY_PHONG_VAN_V2;

            if (!hasSchedule && !isCancelled) return null;

            return {
                round: Number(round) || (candStatus === HUY_PHONG_VAN_V2 ? 2 : 1),
                roundText: (Number(round) === 2 || candStatus === HUY_PHONG_VAN_V2) ? "Phỏng vấn Vòng 2" : "Phỏng vấn Vòng 1",
                date: date || "",
                dateFormatted: date ? moment(date).format("DD/MM/YYYY") : "—",
                startTime: startTime ? toHm(startTime) : "",
                endTime: endTime ? toHm(endTime) : "",
                timeRange: startTime
                    ? `${toHm(startTime)}${endTime ? " - " + toHm(endTime) : ""}`
                    : "—",
            };
        },
        hasScheduleInput() {
            return Boolean(this.interviewScheduleForm.date && this.interviewScheduleForm.start_time);
        },
        interviewSchedulePreview() {
            const f = this.interviewScheduleForm;
            if (!f.date || !f.start_time) return "";
            const d = moment(f.date, "YYYY-MM-DD");
            if (!d.isValid()) return "";
            const dayNames = ["Chủ nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];
            const dayText = dayNames[d.day()];
            const roundText = Number(f.round) === 2 ? "Vòng 2" : "Vòng 1";
            return `Lịch PV ${roundText}: ${dayText}, ${d.format("DD/MM/YYYY")} từ ${f.start_time}${f.end_time ? " đến " + f.end_time : ""}`;
        },
        candidateHasOfferInfo() {
            if (!this.candidate) return false;
            const c = this.candidate;
            return Boolean(
                c.salary ||
                c.expected_salary ||
                c.start_date ||
                c.offer_note ||
                this.candidateStatus === CAN_LAM_THU_TUC_PASS ||
                this.candidateStatus === UNG_VIEN_KHONG_DI_LAM
            );
        },
        isOfficialRecruitment() {
            if (!this.candidate) return true;
            if (Number(this.candidate.type) === 1) return false;
            const found = (this.orders || []).find((o) =>
                String(o.id) === String(this.candidate.job_id) ||
                String(o.id) === String(this.candidate.order_id) ||
                String(o.id) === String(this.candidate.raw_job_id) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(this.candidate.job_id)) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(this.candidate.raw_job_id))
            );
            if (found && (Number(found.type) === 2 || String(found.type).toLowerCase() === "teacher")) {
                return false;
            }
            return true;
        },
    },
    watch: {
        visible(val) {
            if (val) {
                this.activeTab = this.initialTab || "info";
                this.initSelectedMedia();
                this.initScheduleFromCandidate(true);
                this.$nextTick(() => {
                    if (this.$refs.stageProgressRef) {
                        this.$refs.stageProgressRef.scrollToActive();
                        setTimeout(() => this.$refs.stageProgressRef && this.$refs.stageProgressRef.scrollToActive(), 80);
                        setTimeout(() => this.$refs.stageProgressRef && this.$refs.stageProgressRef.scrollToActive(), 250);
                    }
                });
            }
        },
        initialTab(val) {
            if (val) this.activeTab = val;
        },
        candidate: {
            deep: true,
            handler() {
                this.initSelectedMedia();
                this.initScheduleFromCandidate();
            },
        },
    },
    methods: {
        formatMoney(num) {
            if (num === null || num === undefined || num === "") return "0";
            return Number(num).toLocaleString("vi-VN");
        },
        openProbationDialog() {
            if (!this.candidate) return;
            if (this.isHrDepartment) {
                this.$message.warning("Chỉ Teamlead mới có quyền điền kết quả thử việc");
                return;
            }
            this.$emit("open-probation", this.candidate);
        },
        submitProbationResult() {
            this.$refs.probationFormRef.validate((valid) => {
                if (!valid) return;
                const isPass = this.probationForm.result === "pass";
                const targetStatus = isPass
                    ? DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS
                    : DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL;

                const updatedCandidate = {
                    ...this.candidate,
                    probation_result: this.probationForm.result,
                    official_salary: isPass ? this.probationForm.official_salary : null,
                    probation_note: this.probationForm.note,
                };

                this.$emit("update-status", {
                    id: this.candidate.id,
                    status: targetStatus,
                    note: this.probationForm.note
                        ? `[Đánh giá thử việc: ${isPass ? 'ĐẠT (Pass)' : 'KHÔNG ĐẠT (Failed)'}${this.probationForm.official_salary ? ' - Lương: ' + this.formatMoney(this.probationForm.official_salary) + ' VNĐ' : ''}]: ${this.probationForm.note}`
                        : `[Đánh giá thử việc: ${isPass ? 'ĐẠT (Pass)' : 'KHÔNG ĐẠT (Failed)'}${this.probationForm.official_salary ? ' - Lương: ' + this.formatMoney(this.probationForm.official_salary) + ' VNĐ' : ''}]`,
                    candidate: updatedCandidate,
                });

                this.probationDialogVisible = false;
            });
        },
        initSelectedMedia() {
            if (this.candidate) {
                this.selectedMediaUrl =
                    this.candidate.cv_file ||
                    (this.candidate.media && this.candidate.media[0] && (this.candidate.media[0].url || this.candidate.media[0].original_url)) ||
                    "";
            } else {
                this.selectedMediaUrl = "";
            }
        },
        typeLabel(type) {
            const num = Number(type);
            if (num === 1) return "Tuyển hộ (Giáo viên)";
            return "Tuyển chính thức";
        },
        jobTitle(jobIdOrCandidate) {
            if (!jobIdOrCandidate) return "";
            if (typeof jobIdOrCandidate === "object") {
                const found = this.orders.find((o) =>
                    String(o.id) === String(jobIdOrCandidate.job_id) ||
                    String(o.id) === String(jobIdOrCandidate.order_id) ||
                    String(o.id) === String(jobIdOrCandidate.raw_job_id) ||
                    (o.recruitment_job_id && String(o.recruitment_job_id) === String(jobIdOrCandidate.job_id)) ||
                    (o.recruitment_job_id && String(o.recruitment_job_id) === String(jobIdOrCandidate.raw_job_id))
                );
                return found ? (found.title || found.position_title || "") : (jobIdOrCandidate.order_title || "");
            }
            const found = this.orders.find((o) =>
                String(o.id) === String(jobIdOrCandidate) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(jobIdOrCandidate))
            );
            return found ? (found.title || found.position_title || "") : "";
        },
        formatDate(v) {
            return v ? moment(v).format("DD/MM/YYYY HH:mm") : "—";
        },
        formatFileSize(bytes) {
            if (!bytes || isNaN(bytes)) return "";
            const num = Number(bytes);
            if (num < 1024) return `${num} B`;
            if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
            return `${(num / (1024 * 1024)).toFixed(1)} MB`;
        },
        copyText(text) {
            if (!text) return;
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$message.success(`Đã sao chép: ${text}`);
                });
            } else {
                const input = document.createElement("input");
                input.value = text;
                document.body.appendChild(input);
                input.select();
                document.execCommand("copy");
                document.body.removeChild(input);
                this.$message.success(`Đã sao chép: ${text}`);
            }
        },
        approveStatusText(status) {
            const num = Number(status);
            if (num === 1) return "Đã duyệt";
            if (num === 2) return "Từ chối";
            return "Chờ duyệt";
        },
        approveStatusIcon(status) {
            const num = Number(status);
            if (num === 1) return "el-icon-circle-check";
            if (num === 2) return "el-icon-circle-close";
            return "el-icon-time";
        },
        approveStatusClass(status) {
            const num = Number(status);
            if (num === 1) return "rcv-pill--approved";
            if (num === 2) return "rcv-pill--rejected";
            return "rcv-pill--pending";
        },
        handleApproveClick(role, status) {
            const isHr = role === "hr";
            const roleName = isHr ? "HR" : "Teamlead";
            const isReject = Number(status) === 2;
            const candidateName = (this.candidate && this.candidate.name) || `#${(this.candidate && this.candidate.id) || ""}`;

            if (isReject) {
                const promptMsg = isHr
                    ? "Nhập lý do HR từ chối hồ sơ ứng viên (bắt buộc):"
                    : "Nhập lý do Teamlead từ chối hồ sơ ứng viên (bắt buộc):";
                const promptTitle = `Xác nhận ${roleName} từ chối hồ sơ`;

                this.$prompt(promptMsg, promptTitle, {
                    confirmButtonText: "Xác nhận từ chối",
                    cancelButtonText: "Huỷ",
                    inputType: "textarea",
                    inputPlaceholder: "Nhập chi tiết lý do từ chối...",
                    inputPattern: /\S+/,
                    inputErrorMessage: "Vui lòng nhập lý do từ chối",
                })
                    .then(({ value }) => {
                        const reason = (value || "").trim();
                        const payload = {
                            id: this.candidate.id,
                            candidate: this.candidate,
                        };
                        if (isHr) {
                            payload.status = HR_TU_CHOI;
                            payload.hr_reason_reject = reason;
                            payload.reject_reason = reason;
                            payload.note = reason;
                        } else {
                            payload.status = TEAM_LEAD_TU_CHOI;
                            payload.team_lead_reason_reject = reason;
                            payload.reject_reason = reason;
                            payload.note = reason;
                        }
                        this.$emit("update-status", payload);
                    })
                    .catch(() => {});
                return;
            }

            // Trường hợp HR duyệt -> mở modal Gửi Duyệt CV: Chuyển Teamlead duyệt để nhập thông tin sơ vấn
            if (isHr) {
                this.$emit("open-preliminary", this.candidate);
                return;
            }

            // Trường hợp Teamlead Duyệt -> mở dialog xếp lịch phỏng vấn (round, date, start_time, end_time)
            this.openInterviewScheduleDialog();
        },
        initScheduleFromCandidate(force = false) {
            if (!this.candidate) return;
            const cand = this.candidate;
            if (!force && this._currentCandidateId === cand.id && (this.interviewScheduleForm.date || this.interviewScheduleForm.start_time)) {
                return;
            }
            this._currentCandidateId = cand.id;
            const candStatus = Number(cand.status);
            let defaultRound = cand.round
                ? Number(cand.round)
                : (candStatus === DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS || candStatus >= PHONG_VAN_V2 || candStatus === HUY_PHONG_VAN_V2 ? 2 : 1);

            const existingDate = cand.date || cand.interview_date || (cand.interview_data && (cand.interview_data.date || cand.interview_data.interview_date)) || "";
            const existingStart = toHm(cand.start_time || cand.interview_start_time || (cand.interview_data && (cand.interview_data.start_time || cand.interview_data.interview_start_time))) || "";
            const existingEnd = toHm(cand.end_time || cand.interview_end_time || (cand.interview_data && (cand.interview_data.end_time || cand.interview_data.interview_end_time))) || "";

            this.interviewScheduleForm = {
                round: defaultRound,
                date: existingDate,
                start_time: existingStart,
                end_time: existingEnd,
            };
        },
        openInterviewScheduleDialog(targetRound) {
            if (!this.candidate) return;
            const cand = this.candidate;
            const candStatus = Number(cand.status);
            let defaultRound = targetRound !== undefined && targetRound !== null ? Number(targetRound) : null;
            if (!defaultRound) {
                defaultRound = (this.interviewScheduleForm && this.interviewScheduleForm.round)
                    || (cand.round ? Number(cand.round) : (candStatus === DA_NHAP_KET_QUA_PHONG_VAN_V1_PASS || candStatus >= PHONG_VAN_V2 || candStatus === HUY_PHONG_VAN_V2 ? 2 : 1));
            }

            // Nếu form đã có dữ liệu do HR đang nhập thì ưu tiên giữ lại, nếu chưa có thì nạp từ candidate
            const existingDate = (this.interviewScheduleForm && this.interviewScheduleForm.date) || cand.date || cand.interview_date || "";
            const existingStart = (this.interviewScheduleForm && this.interviewScheduleForm.start_time) || toHm(cand.start_time || cand.interview_start_time) || "";
            const existingEnd = (this.interviewScheduleForm && this.interviewScheduleForm.end_time) || toHm(cand.end_time || cand.interview_end_time) || "";

            this.interviewScheduleForm = {
                round: defaultRound,
                date: existingDate,
                start_time: existingStart,
                end_time: existingEnd,
            };

            this.interviewScheduleDialogVisible = true;
            this.$nextTick(() => {
                if (this.$refs.interviewScheduleFormRef) {
                    this.$refs.interviewScheduleFormRef.clearValidate();
                }
            });
        },
        handleCancelInterview(round = 1) {
            if (!this.candidate) return;
            if (!this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền huỷ lịch phỏng vấn");
                return;
            }
            const roundNum = Number(round) || 1;
            const targetStatus = roundNum === 2 ? HUY_PHONG_VAN_V2 : HUY_PHONG_VAN_V1;
            const roundName = roundNum === 2 ? "Vòng 2" : "Vòng 1";
            const candidateName = this.candidate.name || `#${this.candidate.id}`;

            this.$prompt(
                `Nhập lý do huỷ lịch tham gia phỏng vấn ${roundName} của ứng viên "${candidateName}":`,
                `Xác nhận huỷ lịch tham gia phỏng vấn ${roundName}`,
                {
                    confirmButtonText: "Xác nhận huỷ lịch",
                    cancelButtonText: "Đóng",
                    inputType: "textarea",
                    inputPlaceholder: "VD: Ứng viên bận việc đột xuất xin dời, Không liên lạc được, Ứng viên từ chối tham gia...",
                    inputPattern: /\S+/,
                    inputErrorMessage: "Vui lòng nhập lý do huỷ lịch phỏng vấn",
                    confirmButtonClass: "el-button--danger",
                }
            )
                .then(({ value }) => {
                    const reason = (value || "").trim();
                    const noteText = `[Huỷ lịch PV ${roundName}]: ${reason}`;
                    const payload = {
                        id: this.candidate.id,
                        status: targetStatus,
                        round: roundNum,
                        cancel_reason: reason,
                        reason: reason,
                        note: noteText,
                        candidate: {
                            ...this.candidate,
                            status: targetStatus,
                            cancel_reason: reason,
                            ...(roundNum === 1
                                ? { cancel_interview_v1_reason: reason }
                                : { cancel_interview_v2_reason: reason }),
                        },
                    };
                    this.$emit("update-status", payload);
                })
                .catch(() => {});
        },
        fillDefaultSchedule() {
            this.interviewScheduleForm.date = moment().format("YYYY-MM-DD");
            this.interviewScheduleForm.start_time = "14:00";
            this.interviewScheduleForm.end_time = "15:00";
            if (this.$refs.interviewScheduleFormRef) {
                this.$refs.interviewScheduleFormRef.clearValidate();
            }
            if (this.$refs.hrInterviewScheduleInlineFormRef) {
                this.$refs.hrInterviewScheduleInlineFormRef.clearValidate();
            }
        },
        clearScheduleFields() {
            this.interviewScheduleForm.date = "";
            this.interviewScheduleForm.start_time = "";
            this.interviewScheduleForm.end_time = "";
            if (this.$refs.interviewScheduleFormRef) {
                this.$refs.interviewScheduleFormRef.clearValidate();
            }
            if (this.$refs.hrInterviewScheduleInlineFormRef) {
                this.$refs.hrInterviewScheduleInlineFormRef.clearValidate();
            }
        },
        onInterviewStartTimeChange(val) {
            if (!val) return;
            const match = String(val).match(/^(\d{1,2}):(\d{2})/);
            if (!match) return;
            let hour = parseInt(match[1], 10);
            let min = parseInt(match[2], 10);
            hour += 1;
            if (hour > 23) {
                hour = 23;
                min = 59;
            }
            const endHourStr = String(hour).padStart(2, "0");
            const endMinStr = String(min).padStart(2, "0");
            this.interviewScheduleForm.end_time = `${endHourStr}:${endMinStr}`;
        },
        submitHrInterviewSchedule() {
            if (!this.candidate) return;
            const formRef = this.$refs.hrInterviewScheduleInlineFormRef;
            if (!formRef) {
                this.submitInterviewSchedule();
                return;
            }
            formRef.validate((valid) => {
                if (!valid) return;
                const roundNum = Number(this.interviewScheduleForm.round) || 1;
                const hasSchedule = Boolean(
                    this.interviewScheduleForm.date && this.interviewScheduleForm.start_time
                );
                if (!hasSchedule) {
                    this.$message.warning("Vui lòng chọn ngày và giờ bắt đầu phỏng vấn");
                    return;
                }
                const startTimeHms = toHms(this.interviewScheduleForm.start_time);
                const endTimeHms = this.interviewScheduleForm.end_time
                    ? toHms(this.interviewScheduleForm.end_time)
                    : null;

                const targetStatus = roundNum === 2 ? PHONG_VAN_V2 : PHONG_VAN_V1;

                const payload = {
                    id: this.candidate.id,
                    status: targetStatus,
                    round: roundNum,
                    date: this.interviewScheduleForm.date,
                    start_time: startTimeHms,
                    end_time: endTimeHms,
                    candidate: {
                        ...this.candidate,
                        status: targetStatus,
                        round: roundNum,
                        date: this.interviewScheduleForm.date,
                        start_time: startTimeHms,
                        end_time: endTimeHms,
                    },
                };

                this.$emit("update-status", payload);
            });
        },
        submitInterviewSchedule() {
            if (!this.candidate) return;
            this.$refs.interviewScheduleFormRef.validate((valid) => {
                if (!valid) return;
                const roundNum = Number(this.interviewScheduleForm.round) || 1;

                const hasSchedule = Boolean(
                    this.interviewScheduleForm.date && this.interviewScheduleForm.start_time
                );

                if (this.isHrDepartment && !hasSchedule) {
                    this.$message.warning("Vui lòng chọn ngày và giờ bắt đầu phỏng vấn");
                    return;
                }

                const startTimeHms = hasSchedule ? toHms(this.interviewScheduleForm.start_time) : null;
                const endTimeHms = hasSchedule && this.interviewScheduleForm.end_time
                    ? toHms(this.interviewScheduleForm.end_time)
                    : null;

                // Nếu có nhập lịch: Vòng 1 -> status 5 (PHONG_VAN_V1), Vòng 2 -> status 9 (PHONG_VAN_V2)
                // Nếu Teamlead duyệt không nhập lịch -> status 2
                const targetStatus = roundNum === 2
                    ? PHONG_VAN_V2
                    : (hasSchedule ? PHONG_VAN_V1 : 2);

                const payload = {
                    id: this.candidate.id,
                    status: targetStatus,
                    ...(this.isHrDepartment ? {} : { teamlead_approve: 1 }),
                    candidate: {
                        ...this.candidate,
                        ...(this.isHrDepartment ? {} : { teamlead_approve: 1 }),
                        status: targetStatus,
                    },
                };

                // Chỉ gửi kèm 3 field (date, start_time, end_time) và round vào payload khi có nhập
                if (hasSchedule) {
                    payload.round = roundNum;
                    payload.date = this.interviewScheduleForm.date;
                    payload.start_time = startTimeHms;
                    payload.end_time = endTimeHms;

                    payload.candidate.round = roundNum;
                    payload.candidate.date = this.interviewScheduleForm.date;
                    payload.candidate.start_time = startTimeHms;
                    payload.candidate.end_time = endTimeHms;
                }

                this.$emit("update-status", payload);
                this.interviewScheduleDialogVisible = false;
            });
        },
        quickApprove(targetStatus, actionTitle = "") {
            const meta = getStatusMeta(targetStatus);
            const title = actionTitle || `Chuyển sang: ${meta.label}`;
            this.$confirm(`Bạn có chắc chắn muốn duyệt và ${title.toLowerCase()}?`, "Xác nhận duyệt hồ sơ ứng viên", {
                confirmButtonText: "Đồng ý",
                cancelButtonText: "Huỷ",
                type: "success",
            })
                .then(() => {
                    this.submitStatusChange(targetStatus);
                })
                .catch(() => {});
        },
        openRejectDialog(status) {
            const meta = getStatusMeta(status);
            this.$prompt("Nhập lý do từ chối hồ sơ ứng viên (tuỳ chọn):", `Xác nhận từ chối (${meta.label})`, {
                confirmButtonText: "Từ chối hồ sơ",
                cancelButtonText: "Huỷ",
                inputType: "textarea",
                inputPlaceholder: "Nhập lý do hoặc ghi chú từ chối...",
            })
                .then(({ value }) => {
                    this.submitStatusChange(status, value);
                })
                .catch(() => {});
        },
        handleSelectStatus(status) {
            const num = Number(status);
            if ((num === HUY_PHONG_VAN_V1 || num === HUY_PHONG_VAN_V2) && !this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền huỷ lịch phỏng vấn");
                return;
            }
            if (num === DA_NHAN_VIEC && !this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền cập nhật trạng thái Đã nhận việc");
                return;
            }
            const meta = getStatusMeta(num);
            this.$confirm(`Chuyển trạng thái tiến trình ứng viên sang "${meta.label}" [#${num}]?`, "Cập nhật tiến trình", {
                confirmButtonText: "Cập nhật",
                cancelButtonText: "Huỷ",
                type: "info",
            })
                .then(() => {
                    this.submitStatusChange(num);
                })
                .catch(() => {});
        },
        submitStatusChange(status, note = "") {
            if (!this.candidate) return;
            const num = Number(status);
            if ((num === HUY_PHONG_VAN_V1 || num === HUY_PHONG_VAN_V2) && !this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền huỷ lịch phỏng vấn");
                return;
            }
            if (num === DA_NHAN_VIEC && !this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền cập nhật trạng thái Đã nhận việc");
                return;
            }
            this.$emit("update-status", {
                id: this.candidate.id,
                status: Number(status),
                note: (note || "").trim(),
                candidate: this.candidate,
            });
        },
        close() {
            this.$emit("update:visible", false);
        },
        onOpened() {
            if (this.$refs.stageProgressRef) {
                this.$refs.stageProgressRef.scrollToActive();
            }
        },
        onClosed() {
            this.activeTab = "info";
            this.selectedMediaUrl = "";
        },
        handleEdit() {
            const current = this.candidate;
            this.close();
            this.$emit("edit", current);
        },
        openOfferDialog() {
            if (!this.candidate) return;
            if (this.isHrDepartment) {
                this.$message.warning("Chỉ Teamlead mới có quyền điền thông tin Thử việc & Offer");
                return;
            }
            this.$emit("open-offer", this.candidate);
        },
        openTrainingDialog() {
            if (!this.candidate) return;
            if (!this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền nhập kết quả đào tạo hội nhập");
                return;
            }
            this.$emit("open-training-evaluation", this.candidate);
        },
        handleMarkHired() {
            if (!this.candidate) return;
            if (!this.isHrDepartment) {
                this.$message.warning("Chỉ HR mới có quyền cập nhật trạng thái Đã nhận việc");
                return;
            }
            const name = this.candidate.name || this.candidate.candidate_name || "ứng viên";
            this.$confirm(`Xác nhận ứng viên "${name}" ĐÃ NHẬN VIỆC chính thức?`, "Xác nhận Đã nhận việc", {
                confirmButtonText: "Xác nhận Đã nhận việc",
                cancelButtonText: "Huỷ",
                type: "success",
                confirmButtonClass: "el-button--success",
            })
                .then(() => {
                    this.$emit("update-status", {
                        id: this.candidate.id,
                        status: DA_NHAN_VIEC,
                        note: "[HR xác nhận]: Ứng viên đã nhận việc chính thức",
                        message: "[HR xác nhận]: Ứng viên đã nhận việc chính thức",
                        candidate: {
                            ...this.candidate,
                            status: DA_NHAN_VIEC,
                        },
                    });
                })
                .catch(() => {});
        },
    },
};
</script>

<style scoped>
.rcv-detail__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 12px;
    margin-bottom: 14px;
    border-bottom: 1px solid #f1f5f9;
}
.rcv-detail__id {
    font-size: 12px;
    color: #4f46e5;
    font-weight: 700;
    margin-bottom: 2px;
}
.rcv-detail__name {
    font-size: 18px;
    font-weight: 800;
    color: #1e293b;
}
.rcv-detail__head-right {
    display: flex;
    align-items: center;
    gap: 8px;
}
.rcv-detail__status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 700;
    border: 1px solid;
}
.rcv-detail__job-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    background: #eef2ff;
    color: #4338ca;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}
.rcv-detail__type-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    background: #f1f5f9;
    color: #475569;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
}
.rcv-detail__type-badge--1 {
    background: #fef3c7;
    color: #b45309;
}

/* Thanh thao tác duyệt hồ sơ */
.rcv-detail__approval-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px 14px;
    margin-bottom: 14px;
}
.rcv-detail__approval-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.rcv-detail__approval-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
}
.rcv-detail__approval-badge i {
    color: #4f46e5;
    font-size: 15px;
}
.rcv-detail__approval-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    border: 1px solid;
}
.rcv-approve-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 11.5px;
    font-weight: 700;
    border: 1px solid;
    transition: all 0.2s;
}
.rcv-approve-role {
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    opacity: 0.85;
}
.rcv-approve-pill.rcv-pill--pending {
    background: #fffbeb;
    color: #d97706;
    border-color: #fde68a;
}
.rcv-approve-pill.rcv-pill--approved {
    background: #ecfdf5;
    color: #059669;
    border-color: #a7f3d0;
}
.rcv-approve-pill.rcv-pill--rejected {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fecaca;
}
.rcv-reject-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: #fef2f2;
    border: 1px solid #fee2e2;
    border-left: 4px solid #dc2626;
    border-radius: 8px;
    padding: 8px 12px;
    margin-bottom: 12px;
    color: #991b1b;
    font-size: 12.5px;
    line-height: 1.4;
}
.rcv-reject-banner i {
    color: #dc2626;
    font-size: 16px;
    margin-top: 1px;
}
.rcv-reject-banner__title {
    font-weight: 700;
    margin-right: 4px;
}
.rcv-detail__approval-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.rcv-dropdown-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    vertical-align: middle;
}
.rcv-status-dropdown-menu {
    max-height: 360px;
    overflow-y: auto;
}

.rcv-detail__tabs /deep/ .el-tabs__header {
    margin-bottom: 16px;
}
.rcv-tab-badge {
    display: inline-block;
    padding: 0 6px;
    font-size: 10px;
    font-weight: 700;
    border-radius: 4px;
    background: #eef2ff;
    color: #4f46e5;
    margin-left: 4px;
}
.rcv-detail__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 20px;
    background: #f8fafc;
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 16px;
}
.rcv-detail__field {
    display: flex;
    flex-direction: column;
    gap: 3px;
}
.rcv-detail__label {
    font-size: 11.5px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
}
.rcv-detail__val {
    font-size: 13.5px;
    color: #1e293b;
}
.rcv-detail__contact {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.rcv-detail__contact a {
    color: #1e293b;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.15s;
}
.rcv-detail__contact a:hover {
    color: #4f46e5;
    text-decoration: underline;
}
.rcv-copy-btn {
    cursor: pointer;
    color: #94a3b8;
    margin-left: 4px;
    font-size: 12px;
}
.rcv-copy-btn:hover {
    color: #4f46e5;
}
.rcv-detail__block {
    margin-bottom: 16px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px 16px;
}
.rcv-detail__block-title {
    font-size: 13px;
    font-weight: 700;
    color: #334155;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
}
.rcv-detail__block-title i {
    color: #4f46e5;
}
.rcv-detail__message-box {
    font-size: 13.5px;
    color: #334155;
    line-height: 1.6;
    background: #f8fafc;
    padding: 10px 12px;
    border-radius: 6px;
    white-space: pre-wrap;
    border-left: 3px solid #4f46e5;
}
.rcv-detail__empty-text {
    font-size: 12.5px;
    color: #94a3b8;
    font-style: italic;
}
.rcv-detail__media-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.rcv-detail__media-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    background: #f8fafc;
    border-radius: 6px;
    font-size: 12.5px;
}
.rcv-detail__media-name {
    color: #334155;
    font-weight: 500;
}
.rcv-detail__media-link {
    color: #4f46e5;
    text-decoration: none;
    font-weight: 600;
}
.rcv-detail__media-size {
    font-size: 11px;
    color: #94a3b8;
    margin-left: auto;
    margin-right: 12px;
}
.rcv-detail__media-picker {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 8px 12px;
    background: #f1f5f9;
    border-radius: 8px;
    margin-bottom: 8px;
}
.rcv-detail__media-picker-label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
}
.rcv-detail__cv-pane {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.rcv-detail__cv-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: #f8fafc;
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}
.rcv-detail__cv-url {
    font-size: 12px;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 450px;
}
.rcv-detail__cv-btns {
    display: flex;
    align-items: center;
    gap: 6px;
}
.rcv-detail__pdf-frame {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    background: #333;
}
.rcv-pdf-iframe {
    display: block;
    width: 100%;
    min-height: 560px;
}
.rcv-detail__no-cv {
    text-align: center;
    padding: 40px 20px;
    color: #94a3b8;
}
.rcv-detail__no-cv i {
    font-size: 40px;
    margin-bottom: 10px;
    color: #cbd5e1;
}
.rcv-detail__no-cv p {
    font-size: 13.5px;
    margin-bottom: 16px;
}
.rcv-muted {
    color: #94a3b8;
}
.rcv-detail-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}
.rcv-detail-footer__left {
    font-size: 12px;
    color: #94a3b8;
}
.rcv-detail-footer__right {
    display: flex;
    align-items: center;
    gap: 8px;
}

/* Khối Kết quả Phỏng vấn V1 & V2 */
.rcv-detail__block--interview-v1 {
    border-color: #c7d2fe;
    background: #f8fafc;
}
.rcv-detail__block--interview-v2 {
    border-color: #ddd6fe;
    background: #faf5ff;
}
.text-indigo {
    color: #4338ca !important;
}
.text-purple {
    color: #6b21a8 !important;
}

/* Khối Kết quả thử việc & Mức lương */
.rcv-detail__block--probation {
    border-color: #c7d2fe;
    background: #f5f3ff;
}
.rcv-detail__grid--probation {
    background: #fff;
    border: 1px solid #e0e7ff;
    margin-bottom: 0;
}
.rcv-badge-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    width: fit-content;
}
.rcv-badge-pill--pass {
    background: #dcfce7;
    color: #15803d;
    border: 1px solid #86efac;
}
.rcv-badge-pill--fail {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fca5a5;
}
.rcv-probation-dialog {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.rcv-probation-cand {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px 14px;
}
.rcv-probation-cand__name {
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
}
.rcv-probation-cand__sub {
    font-size: 12.5px;
    color: #64748b;
    margin-top: 2px;
}
.rcv-probation-radio {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}
.rcv-probation-radio /deep/ .el-radio-button {
    width: 100%;
}
.rcv-probation-radio /deep/ .el-radio-button__inner {
    width: 100%;
    border-radius: 6px !important;
    border: 1px solid #dcdfe6;
}
.rcv-form__hint {
    font-size: 11.5px;
    color: #6366f1;
    margin-top: 4px;
}
.rcv-detail__source-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 999px;
    line-height: 1.4;
}
.rcv-detail__source-badge--apply,
.rcv-source-pill--apply {
    background: #ecfdf5;
    color: #059669;
    border: 1px solid #a7f3d0;
}
.rcv-detail__source-badge--web,
.rcv-source-pill--web {
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #bfdbfe;
}
.rcv-source-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
}
.rcv-detail__block--preliminary {
    border-left: 3px solid #059669;
    background: #f0fdf4;
    border-radius: 8px;
    padding: 12px 14px;
    margin-top: 14px;
}
.text-emerald {
    color: #059669;
}
.rcv-detail__preliminary-body {
    font-size: 13.5px;
    color: #1e293b;
    line-height: 1.6;
    margin-top: 6px;
    white-space: pre-wrap;
}
.rcv-schedule-intro {
    background: #f0fdf4;
    border: 1px dashed #86efac;
    color: #166534;
    font-size: 12.5px;
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 14px;
    line-height: 1.5;
}
.rcv-form-row {
    display: flex;
    gap: 12px;
}
.rcv-form-col {
    flex: 1;
    margin-bottom: 14px;
}
.rcv-interview-schedule-preview {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1d4ed8;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
}
.rcv-interview-schedule-preview--empty {
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    color: #64748b;
    font-size: 12.5px;
    font-weight: normal;
}
.rcv-detail__block--schedule {
    border-left: 3px solid #0284c7;
    background: #f0f9ff;
    border-radius: 8px;
    padding: 12px 14px;
    margin-top: 14px;
}
.rcv-detail__block--schedule-cancelled {
    border-left: 3px solid #ef4444;
    background: #fef2f2;
    border-radius: 8px;
    padding: 12px 14px;
    margin-top: 14px;
}
.rcv-reject-banner--cancel-interview {
    background: #f8fafc;
    border: 1px solid #cbd5e1;
    color: #475569;
}
.rcv-reject-banner--cancel-interview i {
    color: #ef4444;
}
.text-cyan {
    color: #0284c7;
}
.rcv-preliminary-info-card {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 12px;
}
.rcv-preliminary-info-card__head {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #166534;
    margin-bottom: 6px;
}
.rcv-preliminary-info-card__text {
    font-size: 13px;
    color: #1f2937;
    line-height: 1.5;
    white-space: pre-wrap;
    background: #ffffff;
    border: 1px solid #dcfce7;
    border-radius: 6px;
    padding: 8px 10px;
    max-height: 140px;
    overflow-y: auto;
}
.rcv-preliminary-info-card__empty {
    font-size: 12.5px;
    color: #6b7280;
    font-style: italic;
    display: flex;
    align-items: center;
    gap: 4px;
}
.rcv-schedule-round-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 13.5px;
    color: #1e293b;
}
.rcv-schedule-round-text {
    font-weight: 600;
}
.rcv-schedule-round-badge {
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #bfdbfe;
    font-size: 11.5px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
}
.rcv-detail__block--hr-schedule {
    border: 1px solid #e0e7ff;
    border-left: 3px solid #6366f1;
    background: #faf5ff;
    border-radius: 8px;
    padding: 14px 16px;
    margin-top: 14px;
}
.rcv-hr-schedule-hint {
    background: #ffffff;
    border: 1px dashed #c4b5fd;
    color: #4c1d95;
    font-size: 12.5px;
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 12px;
    line-height: 1.5;
    display: flex;
    align-items: flex-start;
    gap: 6px;
}
.rcv-hr-schedule-hint i {
    color: #7c3aed;
    margin-top: 2px;
}
.rcv-round-radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.rcv-round-radio-group /deep/ .el-radio-button__inner {
    border-radius: 6px !important;
    border: 1px solid #dcdfe6;
    padding: 8px 14px;
    font-weight: 500;
}
.rcv-round-radio-group /deep/ .el-radio-button.is-active .el-radio-button__inner {
    background-color: #4f46e5;
    border-color: #4f46e5;
    color: #fff;
    box-shadow: -1px 0 0 0 #4f46e5;
}

/* Khối Offer */
.rcv-detail__block--offer-pending {
    border: 1px solid #bbf7d0;
    border-left: 3px solid #10b981;
    background: #f0fdf4;
    border-radius: 8px;
    padding: 14px 16px;
    margin-top: 14px;
}
.rcv-detail__block--offer {
    border: 1px solid #99f6e4;
    border-left: 3px solid #0d9488;
    background: #f0fdfa;
    border-radius: 8px;
    padding: 12px 14px;
    margin-top: 14px;
}
.text-teal {
    color: #0f766e !important;
}
.rcv-offer-intro {
    background: #f0fdfa;
    border: 1px dashed #5eead4;
    color: #115e59;
    font-size: 12.5px;
    padding: 8px 12px;
    border-radius: 6px;
    line-height: 1.5;
    display: flex;
    align-items: center;
}
.rcv-offer-status-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
}
.rcv-offer-radio {
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding: 10px 14px !important;
    height: auto !important;
    border-radius: 8px !important;
    display: flex !important;
    align-items: flex-start !important;
}
.rcv-offer-radio__sub {
    font-size: 11.5px;
    color: #64748b;
    margin-top: 3px;
    font-weight: normal;
}
.rcv-offer-radio--pass.is-bordered.is-checked {
    border-color: #10b981 !important;
    background-color: #f0fdf4 !important;
}
.rcv-offer-radio--fail.is-bordered.is-checked {
    border-color: #ef4444 !important;
    background-color: #fef2f2 !important;
}

/* Khối Cần làm thủ tục Pass chờ xác nhận */
.rcv-detail__block--pass-procedure-pending {
    border: 1px solid #99f6e4;
    border-left: 3px solid #0d9488;
    background: #f0fdfa;
    border-radius: 8px;
    padding: 14px 16px;
    margin-top: 14px;
}

/* Bảng tiêu chí đánh giá PV V1 */
.rcv-detail-criteria-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px 12px;
    width: 100%;
}
.rcv-detail-criteria-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
}
.rcv-detail-criteria-table th {
    background: #f1f5f9;
    color: #334155;
    font-size: 11.5px;
    font-weight: 700;
    padding: 7px 10px;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
}
.rcv-detail-criteria-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 12px;
    vertical-align: top;
    line-height: 1.4;
}
.rcv-detail-criteria-table tr:last-child td {
    border-bottom: none;
}
.rcv-detail-criteria-extra {
    font-size: 12px;
    color: #475569;
    padding: 6px 4px 0 4px;
}
.rcv-detail__val--multiline {
    white-space: pre-wrap;
    line-height: 1.5;
}
</style>
